import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, Sparkles, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'caferuis-bc2';

const theme = {
  primary: '#3B82F6',
  secondary: '#E75480',
  bg: '#EFF6FF',
  text: '#1a2e35',
};

const bgImage = "https://static.prod-images.emergentagent.com/jobs/1961affb-6e82-4a8a-b43a-1af358e9a95a/images/c7eb74cbe4e9f40764c180b3ee0d4227ee1657ea1d734243e6ba314b9325b481.png";

const slides = [
  // =============================
  // INTRODUCTION DU BLOC 2
  // =============================

  // SLIDE 0 — TITRE
  {
    type: 'title',
    title: "Bloc 2",
    subtitle: "Manager et gérer les ressources humaines",
    narration: "Bienvenue dans le Bloc 2 du CAFERUIS : Manager et gérer les ressources humaines. Ce bloc couvre l'animation d'équipe, l'organisation du travail, les obligations réglementaires, le développement des compétences, le recrutement et la communication managériale."
  },

  // SLIDE 1 — Présentation du bloc
  {
    type: 'content',
    title: "Présentation du Bloc 2",
    intro: "Le Bloc 2 du CAFERUIS porte sur le management et la gestion des ressources humaines dans le secteur social et médico-social.",
    points: [
      "Enjeux du management dans le secteur social et médico-social",
      "Positionnement du cadre intermédiaire dans la gestion des ressources humaines",
      "Articulation entre management d'équipe et qualité de l'accompagnement des usagers"
    ],
    highlight: "Le responsable d'unité occupe une position stratégique : il doit concilier les exigences institutionnelles, les besoins des équipes et la qualité de l'accompagnement des usagers.",
    narration: "Le Bloc 2 aborde les enjeux du management, le positionnement du cadre intermédiaire et l'articulation entre management d'équipe et qualité de l'accompagnement."
  },

  // SLIDE 2 — SOMMAIRE GÉNÉRAL
  {
    type: 'sommaire',
    title: "Sommaire du Bloc 2",
    sections: [
      { num: "1", title: "Chapitre 1 — Animer une équipe pluridisciplinaire et pluriprofessionnelle", targetSlide: 4 },
      { num: "2", title: "Chapitre 2 — Coordonner l'organisation du travail dans une approche inclusive", targetSlide: 51 },
      { num: "3", title: "Chapitre 3 — Mettre en oeuvre les obligations réglementaires", targetSlide: 93 },
      { num: "4", title: "Chapitre 4 — Développer les compétences individuelles et collectives", targetSlide: 137 },
      { num: "5", title: "Chapitre 5 — Appliquer les procédures de recrutement", targetSlide: 175 },
      { num: "6", title: "Chapitre 6 — Communiquer dans le cadre du management RH", targetSlide: 215 }
    ],
    narration: "Le Bloc 2 couvre 6 chapitres : l'animation d'équipe, l'organisation inclusive du travail, les obligations réglementaires, le développement des compétences, le recrutement et la communication managériale."
  },

  // SLIDE 3 — Articulation management / qualité
  {
    type: 'content',
    title: "Articulation management et qualité",
    intro: "Le management des ressources humaines dans le secteur social et médico-social présente des spécificités liées à la nature des missions exercées.",
    points: [
      "Les équipes sont composées de professionnels issus de disciplines variées",
      "L'accompagnement des usagers repose sur la coopération entre ces professionnels",
      "Le cadre intermédiaire doit garantir la cohérence des interventions",
      "La qualité du management impacte directement la qualité de l'accompagnement"
    ],
    highlight: "Le management ne se limite pas à la dimension organisationnelle. Il comporte une dimension humaine et relationnelle essentielle dans le secteur social.",
    narration: "Le management dans le social implique une articulation entre organisation, accompagnement des équipes et qualité du service rendu aux usagers."
  },

  // =============================
  // CHAPITRE 1 — Animer une équipe pluridisciplinaire
  // =============================

  // SLIDE 4 — Transition Chapitre 1
  { type: 'section', title: "Chapitre 1", subtitle: "Animer une équipe pluridisciplinaire et pluriprofessionnelle",
    narration: "Chapitre 1 : Animer une équipe pluridisciplinaire et pluriprofessionnelle." },

  // SLIDE 5 — Introduction Chapitre 1
  {
    type: 'content',
    title: "Introduction — Chapitre 1",
    intro: "Dans le secteur social et médico-social, l'accompagnement des usagers repose sur l'intervention coordonnée de professionnels issus de différentes disciplines : éducateurs spécialisés, travailleurs sociaux, psychologues, personnels soignants, agents administratifs ou encore personnels logistiques.",
    intro2: "Cette diversité professionnelle constitue une richesse pour la prise en charge globale des usagers, mais elle peut également générer des difficultés liées aux différences de culture professionnelle, de référentiels théoriques ou de pratiques.",
    highlight: "Le responsable d'unité doit être capable de manager une équipe pluriprofessionnelle, de favoriser la coopération entre les différents métiers et de soutenir une dynamique collective orientée vers les objectifs du service.",
    narration: "L'accompagnement des usagers repose sur des professionnels de disciplines variées. Le responsable d'unité doit manager cette équipe pluriprofessionnelle et favoriser la coopération."
  },

  // SLIDE 6 — Animer une équipe
  {
    type: 'content',
    title: "Animer une équipe : les dimensions clés",
    intro: "Animer une équipe ne consiste pas uniquement à organiser le travail. Il s'agit également de :",
    points: [
      "Mobiliser les professionnels autour d'un projet commun",
      "Valoriser les compétences de chacun",
      "Réguler les tensions éventuelles",
      "Soutenir la motivation et l'engagement des équipes"
    ],
    narration: "Animer une équipe implique de mobiliser autour d'un projet commun, valoriser les compétences, réguler les tensions et soutenir la motivation."
  },

  // SLIDE 7 — Objectifs pédagogiques
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 1",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Adapter son style de management aux situations et aux personnes",
      "Comprendre les mécanismes de motivation au travail",
      "Mobiliser les professionnels autour d'objectifs communs",
      "Favoriser la coopération entre différents métiers",
      "Développer la cohésion d'équipe et prévenir les tensions"
    ],
    narration: "Les objectifs du Chapitre 1 : adapter son style de management, comprendre la motivation, mobiliser les professionnels, favoriser la coopération et développer la cohésion."
  },

  // SLIDE 8 — Sommaire détaillé Chapitre 1
  {
    type: 'grid',
    title: "Chapitre 1 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les fondamentaux du management dans le secteur social" },
      { num: "2", label: "Les rôles et fonctions du cadre intermédiaire" },
      { num: "3", label: "Autorité, pouvoir et légitimité managériale" },
      { num: "4", label: "Les styles de leadership et le management situationnel" },
      { num: "5", label: "Les théories de la motivation au travail (Maslow, Herzberg)" },
      { num: "6", label: "Les leviers d'engagement des professionnels" },
      { num: "7", label: "La gestion de la diversité professionnelle" },
      { num: "8", label: "La coordination des pratiques pluridisciplinaires" },
      { num: "9", label: "La cohésion d'équipe et le développement du collectif" },
      { num: "10", label: "La prévention de l'usure professionnelle" }
    ],
    narration: "Le Chapitre 1 couvre 10 thèmes : fondamentaux du management, rôles du cadre, autorité et pouvoir, leadership situationnel, motivation, engagement, diversité, coordination, cohésion et prévention de l'usure."
  },

  // ===== SECTION 1 — Les fondamentaux du management =====
  // SLIDE 9
  { type: 'section', title: "1. Les fondamentaux du management dans le secteur social",
    narration: "Les fondamentaux du management dans le secteur social." },

  // SLIDE 10 — Définition du management
  {
    type: 'content',
    title: "Définition du management",
    intro: "Le management peut être défini comme :",
    highlight: "L'ensemble des pratiques visant à organiser, coordonner et mobiliser les ressources humaines afin d'atteindre les objectifs d'une organisation.",
    intro2: "Dans le secteur social et médico-social, le management ne se limite pas à une dimension organisationnelle. Il comporte également une dimension humaine et relationnelle, liée à la nature des missions exercées.",
    intro3: "Le responsable d'unité doit concilier plusieurs dimensions :",
    points: [
      "La gestion de l'activité du service",
      "L'accompagnement des professionnels",
      "La qualité de l'accompagnement des usagers"
    ],
    narration: "Le management consiste à organiser, coordonner et mobiliser les ressources humaines. Dans le social, il comporte une dimension humaine et relationnelle essentielle."
  },

  // ===== SECTION 2 — Les rôles et fonctions du cadre =====
  // SLIDE 11
  { type: 'section', title: "2. Les rôles et fonctions du cadre intermédiaire",
    narration: "Les rôles et fonctions du cadre intermédiaire." },

  // SLIDE 12 — Position du cadre
  {
    type: 'content',
    title: "Le cadre intermédiaire : une position stratégique",
    intro: "Le cadre intermédiaire occupe une position particulière dans l'organisation. Il se situe à l'interface entre la direction et les équipes.",
    highlight: "Ses principales fonctions s'articulent autour de trois dimensions complémentaires : organisationnelle, managériale et stratégique.",
    narration: "Le cadre intermédiaire se situe à l'interface entre la direction et les équipes. Ses fonctions sont organisationnelles, managériales et stratégiques."
  },

  // SLIDE 13 — Fonction organisationnelle
  {
    type: 'content',
    title: "La fonction organisationnelle",
    intro: "Elle consiste à structurer l'activité du service :",
    points: [
      "Planification du travail",
      "Répartition des tâches",
      "Coordination des interventions"
    ],
    narration: "La fonction organisationnelle consiste à planifier le travail, répartir les tâches et coordonner les interventions."
  },

  // SLIDE 14 — Fonction managériale
  {
    type: 'content',
    title: "La fonction managériale",
    intro: "Elle concerne l'animation et l'accompagnement des équipes. Le responsable d'unité doit :",
    points: [
      "Soutenir les professionnels dans leur pratique",
      "Réguler les tensions éventuelles",
      "Favoriser la coopération"
    ],
    narration: "La fonction managériale implique de soutenir les professionnels, réguler les tensions et favoriser la coopération."
  },

  // SLIDE 15 — Fonction stratégique
  {
    type: 'content',
    title: "La fonction stratégique",
    intro: "Le cadre participe également à la mise en oeuvre des orientations de l'établissement. Il contribue notamment :",
    points: [
      "À la mise en oeuvre du projet d'établissement",
      "À l'amélioration des pratiques professionnelles",
      "Au développement de nouvelles actions"
    ],
    narration: "La fonction stratégique implique la mise en oeuvre du projet d'établissement, l'amélioration des pratiques et le développement de nouvelles actions."
  },

  // ===== SECTION 3 — Autorité et pouvoir =====
  // SLIDE 16
  { type: 'section', title: "3. Autorité, pouvoir et légitimité managériale",
    narration: "Autorité, pouvoir et légitimité managériale." },

  // SLIDE 17 — Le pouvoir
  {
    type: 'content',
    title: "Le pouvoir",
    intro: "Le pouvoir correspond à la capacité d'influencer ou de contraindre les comportements.",
    intro2: "Il peut être lié :",
    points: [
      "À la position hiérarchique",
      "Au contrôle des ressources",
      "À l'expertise professionnelle"
    ],
    narration: "Le pouvoir correspond à la capacité d'influencer les comportements. Il peut être lié à la position hiérarchique, au contrôle des ressources ou à l'expertise."
  },

  // SLIDE 18 — L'autorité
  {
    type: 'content',
    title: "L'autorité",
    intro: "L'autorité correspond à la reconnaissance de la légitimité du cadre par les membres de l'équipe.",
    intro2: "Elle repose sur plusieurs éléments :",
    points: [
      "La compétence professionnelle",
      "La cohérence des décisions",
      "La capacité à prendre en compte les besoins de l'équipe"
    ],
    highlight: "Dans le management moderne, l'autorité ne repose plus uniquement sur la position hiérarchique mais également sur la capacité à mobiliser et fédérer les professionnels.",
    narration: "L'autorité repose sur la compétence, la cohérence et la prise en compte des besoins de l'équipe. Elle se fonde aujourd'hui davantage sur la capacité à fédérer."
  },

  // SLIDE 19 — Comparaison Pouvoir vs Autorité
  {
    type: 'comparison',
    title: "Pouvoir vs Autorité",
    wrong: [
      { label: "Position hiérarchique imposée", correct: "Légitimité reconnue par l'équipe" },
      { label: "Contraindre les comportements", correct: "Mobiliser et fédérer les professionnels" },
      { label: "Contrôle des ressources", correct: "Compétence et cohérence des décisions" },
      { label: "Obéissance par obligation", correct: "Adhésion par confiance et respect" }
    ],
    highlight: "Un management efficace repose davantage sur l'autorité (légitimité) que sur le seul pouvoir (contrainte).",
    narration: "Le pouvoir repose sur la contrainte, l'autorité sur la légitimité. Un management efficace privilégie l'autorité fondée sur la confiance."
  },

  // ===== SECTION 4 — Les styles de leadership =====
  // SLIDE 20
  { type: 'section', title: "4. Les styles de leadership et le management situationnel",
    narration: "Les styles de leadership et le management situationnel." },

  // SLIDE 21 — Le leadership
  {
    type: 'content',
    title: "Le leadership",
    intro: "Le leadership correspond à la capacité d'un responsable à influencer et à mobiliser un groupe afin d'atteindre des objectifs communs.",
    highlight: "Il n'existe pas un seul style de leadership. Le responsable d'unité doit adapter son style de management aux situations et aux professionnels.",
    narration: "Le leadership est la capacité à influencer et mobiliser un groupe. Le responsable doit adapter son style aux situations."
  },

  // SLIDE 22 — Management situationnel
  {
    type: 'content',
    title: "Le management situationnel — Hersey & Blanchard",
    intro: "Le modèle de management situationnel a été développé par Paul Hersey et Kenneth Blanchard.",
    highlight: "Selon ce modèle, le style de management doit s'adapter au niveau d'autonomie et de compétence des professionnels.",
    intro2: "Il distingue quatre styles de management.",
    narration: "Le management situationnel de Hersey et Blanchard propose d'adapter le style de management au niveau d'autonomie et de compétence des professionnels."
  },

  // SLIDE 23 — Les 4 styles de management
  {
    type: 'grid',
    title: "Les 4 styles de management situationnel",
    items: [
      { num: "D", label: "Directif — Consignes précises, contrôle de l'exécution. Adapté aux nouveaux professionnels ou situations d'urgence." },
      { num: "P", label: "Persuasif — Explique les décisions, accompagne la réalisation. Favorise compréhension et adhésion." },
      { num: "Pa", label: "Participatif — Associe les professionnels à la prise de décision. Développe l'implication et l'autonomie." },
      { num: "Dé", label: "Délégatif — Confie une responsabilité importante. Adapté aux professionnels très autonomes." }
    ],
    narration: "Les quatre styles : directif pour les urgences et nouveaux, persuasif pour l'adhésion, participatif pour l'implication, délégatif pour les très autonomes."
  },

  // ===== SECTION 5 — La motivation au travail =====
  // SLIDE 24
  { type: 'section', title: "5. Les théories de la motivation au travail",
    narration: "Les théories de la motivation au travail." },

  // SLIDE 25 — Définition motivation
  {
    type: 'content',
    title: "La motivation au travail",
    intro: "La motivation correspond à l'ensemble des facteurs qui incitent une personne à s'engager dans une activité.",
    highlight: "Dans le secteur social, la motivation des professionnels constitue un élément essentiel pour garantir la qualité de l'accompagnement.",
    narration: "La motivation correspond aux facteurs qui incitent à s'engager. Elle est essentielle pour la qualité de l'accompagnement dans le secteur social."
  },

  // SLIDE 26 — Maslow
  {
    type: 'content',
    title: "La pyramide des besoins de Maslow",
    intro: "Le psychologue Abraham Maslow a proposé une théorie selon laquelle les besoins humains sont organisés en plusieurs niveaux :",
    points: [
      "Besoins physiologiques",
      "Besoins de sécurité",
      "Besoins d'appartenance",
      "Besoins d'estime",
      "Besoins d'accomplissement"
    ],
    highlight: "Dans une organisation, le manager doit veiller à satisfaire ces différents besoins afin de favoriser l'engagement des professionnels.",
    narration: "Maslow hiérarchise les besoins : physiologiques, sécurité, appartenance, estime et accomplissement. Le manager doit veiller à les satisfaire."
  },

  // SLIDE 27 — Herzberg
  {
    type: 'content',
    title: "La théorie des deux facteurs de Herzberg",
    intro: "Frederick Herzberg distingue deux types de facteurs influençant la motivation :",
    points: [
      "Facteurs d'hygiène (environnement) : conditions de travail, rémunération, organisation — leur absence génère de l'insatisfaction",
      "Facteurs de motivation (contenu) : reconnaissance, responsabilité, développement des compétences — ils renforcent l'engagement"
    ],
    highlight: "Pour Herzberg, améliorer les conditions de travail ne suffit pas à motiver. Il faut agir sur le contenu du travail : reconnaissance, responsabilité et développement.",
    narration: "Herzberg distingue les facteurs d'hygiène (conditions de travail) et les facteurs de motivation (reconnaissance, responsabilité). Il faut agir sur les deux."
  },

  // SLIDE 28 — Comparaison Maslow / Herzberg
  {
    type: 'comparison',
    title: "Maslow vs Herzberg : deux regards sur la motivation",
    wrong: [
      { label: "Maslow : besoins physiologiques", correct: "Herzberg : facteurs d'hygiène (conditions de travail)" },
      { label: "Maslow : besoins de sécurité", correct: "Herzberg : facteurs d'hygiène (stabilité de l'emploi)" },
      { label: "Maslow : besoins d'estime", correct: "Herzberg : facteurs de motivation (reconnaissance)" },
      { label: "Maslow : accomplissement", correct: "Herzberg : facteurs de motivation (responsabilité)" }
    ],
    highlight: "Les deux théories se complètent : Maslow met en évidence la hiérarchie des besoins, Herzberg distingue ce qui réduit l'insatisfaction de ce qui crée la motivation.",
    narration: "Maslow et Herzberg se complètent : Maslow hiérarchise les besoins, Herzberg distingue hygiène et motivation."
  },

  // ===== SECTION 6 — Les leviers d'engagement =====
  // SLIDE 29
  { type: 'section', title: "6. Les leviers d'engagement des professionnels",
    narration: "Les leviers d'engagement des professionnels." },

  // SLIDE 30 — Leviers
  {
    type: 'content',
    title: "Les leviers d'engagement",
    intro: "Le responsable d'unité peut mobiliser différents leviers pour soutenir la motivation des équipes :",
    points: [
      "Valoriser les compétences",
      "Reconnaître le travail accompli",
      "Favoriser l'autonomie",
      "Soutenir le développement professionnel"
    ],
    narration: "Les leviers d'engagement : valoriser les compétences, reconnaître le travail, favoriser l'autonomie et soutenir le développement professionnel."
  },

  // ===== SECTION 7 — La gestion de la diversité professionnelle =====
  // SLIDE 31
  { type: 'section', title: "7. La gestion de la diversité professionnelle",
    narration: "La gestion de la diversité professionnelle." },

  // SLIDE 32 — Diversité
  {
    type: 'content',
    title: "La diversité professionnelle",
    intro: "Dans une unité d'intervention sociale, les équipes sont composées de professionnels issus de différentes disciplines.",
    intro2: "Cette diversité peut être source :",
    points: [
      "D'enrichissement",
      "De complémentarité des compétences",
      "De complexité organisationnelle"
    ],
    narration: "La diversité professionnelle est source d'enrichissement et de complémentarité mais aussi de complexité organisationnelle."
  },

  // SLIDE 33 — Cultures professionnelles
  {
    type: 'content',
    title: "Les cultures professionnelles",
    intro: "Chaque profession possède ses propres références :",
    points: [
      "Cadres théoriques",
      "Méthodes d'intervention",
      "Valeurs professionnelles"
    ],
    intro2: "Par exemple :",
    points2: [
      "Les éducateurs peuvent privilégier l'approche éducative",
      "Les soignants peuvent adopter une approche clinique",
      "Les psychologues peuvent mobiliser une analyse psychodynamique"
    ],
    highlight: "Le responsable d'unité doit favoriser la compréhension mutuelle entre ces différentes cultures professionnelles.",
    narration: "Chaque profession a ses propres cadres théoriques et méthodes. Le responsable d'unité doit favoriser la compréhension mutuelle."
  },

  // ===== SECTION 8 — La coordination interprofessionnelle =====
  // SLIDE 34
  { type: 'section', title: "8. La coordination des pratiques pluridisciplinaires",
    narration: "La coordination des pratiques pluridisciplinaires." },

  // SLIDE 35 — Coordination
  {
    type: 'content',
    title: "La coordination interprofessionnelle",
    intro: "La coordination consiste à organiser les interventions des différents professionnels afin de garantir la cohérence de l'accompagnement.",
    intro2: "Elle peut s'appuyer sur plusieurs outils :",
    points: [
      "Réunions d'équipe",
      "Synthèses pluridisciplinaires",
      "Projets personnalisés"
    ],
    narration: "La coordination organise les interventions pour garantir la cohérence. Elle s'appuie sur les réunions d'équipe, les synthèses et les projets personnalisés."
  },

  // ===== SECTION 9 — La cohésion d'équipe =====
  // SLIDE 36
  { type: 'section', title: "9. La cohésion d'équipe et le développement du collectif de travail",
    narration: "La cohésion d'équipe et le développement du collectif de travail." },

  // SLIDE 37 — Définition cohésion
  {
    type: 'content',
    title: "La cohésion d'équipe",
    intro: "La cohésion d'équipe correspond au degré d'unité et de coopération entre les membres d'un groupe.",
    intro2: "Une équipe cohésive se caractérise par :",
    points: [
      "La confiance entre les professionnels",
      "La solidarité",
      "La coopération"
    ],
    narration: "La cohésion d'équipe correspond au degré d'unité et de coopération. Elle se caractérise par la confiance, la solidarité et la coopération."
  },

  // SLIDE 38 — Team building
  {
    type: 'content',
    title: "Les actions de team building",
    intro: "Le team building désigne l'ensemble des actions visant à renforcer la cohésion d'une équipe.",
    intro2: "Il peut s'agir :",
    points: [
      "De temps d'échange informels",
      "D'ateliers collectifs",
      "De projets communs"
    ],
    highlight: "Ces actions permettent de renforcer le sentiment d'appartenance au groupe.",
    narration: "Le team building renforce la cohésion par des temps d'échange, des ateliers collectifs et des projets communs."
  },

  // SLIDE 39 — Gestion des conflits interpersonnels
  {
    type: 'content',
    title: "La gestion des conflits interpersonnels",
    intro: "Les conflits peuvent apparaître dans toute organisation. Ils peuvent être liés :",
    points: [
      "À des divergences de points de vue",
      "À des difficultés de communication",
      "À des tensions organisationnelles"
    ],
    intro2: "Le responsable d'unité doit être capable de :",
    points2: [
      "Analyser la situation",
      "Faciliter le dialogue",
      "Rechercher des solutions"
    ],
    narration: "Les conflits peuvent être liés à des divergences, des difficultés de communication ou des tensions. Le responsable doit analyser, faciliter le dialogue et trouver des solutions."
  },

  // ===== SECTION 10 — Prévention usure professionnelle =====
  // SLIDE 40
  { type: 'section', title: "10. La prévention de l'usure professionnelle",
    narration: "La prévention de l'usure professionnelle." },

  // SLIDE 41 — Usure professionnelle
  {
    type: 'content',
    title: "L'usure professionnelle",
    intro: "Les professionnels du secteur social sont exposés à des situations émotionnellement exigeantes.",
    intro2: "Cette exposition peut entraîner :",
    points: [
      "De la fatigue professionnelle",
      "Un sentiment d'épuisement",
      "Une perte de motivation"
    ],
    narration: "Les professionnels du social sont exposés à des situations émotionnellement exigeantes pouvant entraîner fatigue, épuisement et perte de motivation."
  },

  // SLIDE 42 — Actions de prévention
  {
    type: 'content',
    title: "Actions de prévention de l'usure",
    intro: "Le responsable d'unité peut mettre en place différentes actions de prévention :",
    points: [
      "Espaces d'analyse de pratiques",
      "Supervision professionnelle",
      "Régulation des situations difficiles"
    ],
    narration: "Les actions de prévention : espaces d'analyse de pratiques, supervision professionnelle et régulation des situations difficiles."
  },

  // SLIDE 43 — Conclusion Chapitre 1
  {
    type: 'content',
    title: "Conclusion — Chapitre 1",
    intro: "L'animation d'une équipe pluriprofessionnelle constitue une compétence centrale pour le responsable d'unité.",
    intro2: "Elle suppose de :",
    points: [
      "Adapter son style de management",
      "Soutenir la motivation des professionnels",
      "Favoriser la coopération entre les différents métiers",
      "Renforcer la cohésion d'équipe"
    ],
    highlight: "Ces compétences contribuent à améliorer le fonctionnement du service et la qualité de l'accompagnement proposé aux usagers.",
    narration: "L'animation d'équipe suppose d'adapter le management, soutenir la motivation, favoriser la coopération et renforcer la cohésion."
  },

  // =============================
  // CHAPITRE 2 — Coordonner l'organisation du travail
  // =============================

  // SLIDE 44 — Transition Chapitre 2
  { type: 'section', title: "Chapitre 2", subtitle: "Coordonner l'organisation du travail dans une approche inclusive",
    narration: "Chapitre 2 : Coordonner l'organisation du travail dans une approche inclusive." },

  // SLIDE 45 — Introduction Chapitre 2
  {
    type: 'content',
    title: "Introduction — Chapitre 2",
    intro: "Dans une unité d'intervention sociale, l'organisation du travail constitue un élément déterminant pour assurer la qualité de l'accompagnement des usagers et le bon fonctionnement des équipes.",
    intro2: "Le responsable d'unité doit prendre en compte :",
    points: [
      "Les besoins des usagers",
      "Les contraintes institutionnelles",
      "Les ressources humaines disponibles",
      "Les obligations réglementaires"
    ],
    narration: "L'organisation du travail est déterminante pour la qualité de l'accompagnement. Elle doit prendre en compte besoins des usagers, contraintes et ressources."
  },

  // SLIDE 46 — Enjeux de l'inclusion
  {
    type: 'content',
    title: "L'enjeu de l'inclusion",
    intro: "L'organisation du travail doit intégrer une approche inclusive, qui vise à reconnaître et valoriser la diversité des professionnels.",
    intro2: "L'inclusion concerne notamment :",
    points: [
      "L'égalité entre les femmes et les hommes",
      "L'intégration des travailleurs en situation de handicap",
      "La reconnaissance des parcours professionnels diversifiés"
    ],
    highlight: "Le responsable d'unité doit organiser le travail de manière équitable, inclusive et respectueuse des compétences de chacun.",
    narration: "L'approche inclusive vise à reconnaître la diversité : égalité femmes-hommes, handicap, parcours diversifiés."
  },

  // SLIDE 47 — Objectifs pédagogiques Chap 2
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 2",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Organiser le travail d'une équipe dans une logique inclusive",
      "Concevoir des plannings adaptés aux besoins du service",
      "Anticiper les contraintes organisationnelles",
      "Gérer les absences et les remplacements",
      "Déléguer certaines responsabilités au sein de l'équipe"
    ],
    narration: "Objectifs : organiser le travail de manière inclusive, concevoir des plannings, anticiper les contraintes, gérer les absences et déléguer."
  },

  // SLIDE 48 — Sommaire détaillé Chapitre 2
  {
    type: 'grid',
    title: "Chapitre 2 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les principes de l'inclusion dans le management" },
      { num: "2", label: "Le management de la diversité professionnelle" },
      { num: "3", label: "L'inclusion des travailleurs en situation de handicap" },
      { num: "4", label: "L'égalité professionnelle femmes-hommes" },
      { num: "5", label: "Les principes d'organisation du travail" },
      { num: "6", label: "Les cycles de travail et les roulements" },
      { num: "7", label: "La gestion des astreintes et permanences" },
      { num: "8", label: "Les outils de gestion des plannings" },
      { num: "9", label: "L'anticipation des congés et la gestion de l'absentéisme" },
      { num: "10", label: "Les principes et étapes de la délégation" }
    ],
    narration: "Le Chapitre 2 couvre 10 thèmes : inclusion, diversité, handicap, égalité, organisation du travail, cycles, astreintes, plannings, absentéisme et délégation."
  },

  // ===== SECTION 1 — Inclusion =====
  // SLIDE 49
  { type: 'section', title: "1. Les principes de l'inclusion dans le management",
    narration: "Les principes de l'inclusion dans le management." },

  // SLIDE 50 — Définition inclusion
  {
    type: 'content',
    title: "Définition de l'inclusion",
    intro: "L'inclusion peut être définie comme :",
    highlight: "Une démarche visant à reconnaître et valoriser la diversité des individus afin de permettre à chacun de participer pleinement à la vie collective.",
    intro2: "Dans le monde professionnel, l'inclusion implique :",
    points: [
      "L'égalité des chances",
      "La lutte contre les discriminations",
      "L'adaptation de l'environnement de travail"
    ],
    narration: "L'inclusion vise à reconnaître la diversité pour permettre à chacun de participer pleinement. Elle implique égalité, lutte contre les discriminations et adaptation."
  },

  // ===== SECTION 2 — Management de la diversité =====
  // SLIDE 51
  { type: 'section', title: "2. Le management de la diversité professionnelle",
    narration: "Le management de la diversité professionnelle." },

  // SLIDE 52 — Diversité
  {
    type: 'content',
    title: "Le management de la diversité",
    intro: "Le management de la diversité consiste à prendre en compte les différences entre les individus afin de favoriser leur participation.",
    intro2: "Ces différences peuvent concerner :",
    points: [
      "L'âge",
      "Le genre",
      "L'origine culturelle",
      "Le parcours professionnel",
      "La situation de handicap"
    ],
    highlight: "Le responsable d'unité doit veiller à ce que ces différences ne deviennent pas des facteurs d'exclusion mais constituent une richesse pour l'équipe.",
    narration: "Le management de la diversité prend en compte les différences d'âge, genre, culture, parcours et handicap pour favoriser la participation de chacun."
  },

  // ===== SECTION 3 — Handicap =====
  // SLIDE 53
  { type: 'section', title: "3. L'inclusion des travailleurs en situation de handicap",
    narration: "L'inclusion des travailleurs en situation de handicap." },

  // SLIDE 54 — Handicap
  {
    type: 'content',
    title: "L'inclusion des travailleurs en situation de handicap",
    intro: "La législation française encourage l'intégration des personnes en situation de handicap dans le monde du travail.",
    intro2: "Les établissements doivent notamment :",
    points: [
      "Adapter les postes de travail",
      "Favoriser l'accessibilité des locaux",
      "Mettre en place des aménagements organisationnels si nécessaire"
    ],
    highlight: "Le responsable d'unité doit être attentif à ces questions afin de garantir l'inclusion des professionnels concernés.",
    narration: "La législation encourage l'intégration du handicap : adaptation des postes, accessibilité et aménagements organisationnels."
  },

  // ===== SECTION 4 — Égalité F/H =====
  // SLIDE 55
  { type: 'section', title: "4. L'égalité professionnelle femmes-hommes",
    narration: "L'égalité professionnelle femmes-hommes." },

  // SLIDE 56 — Égalité
  {
    type: 'content',
    title: "L'égalité entre les femmes et les hommes",
    intro: "L'égalité professionnelle constitue un principe fondamental du droit du travail.",
    intro2: "Elle concerne notamment :",
    points: [
      "L'égalité de rémunération",
      "L'accès aux responsabilités",
      "Les conditions de travail"
    ],
    highlight: "Le responsable d'unité doit veiller à prévenir toute forme de discrimination et à favoriser l'égalité entre les professionnels.",
    narration: "L'égalité professionnelle concerne la rémunération, l'accès aux responsabilités et les conditions de travail."
  },

  // ===== SECTION 5 — Organisation du travail =====
  // SLIDE 57
  { type: 'section', title: "5. Les principes d'organisation du travail",
    narration: "Les principes d'organisation du travail dans une unité sociale." },

  // SLIDE 58 — Définition organisation
  {
    type: 'content',
    title: "L'organisation du travail",
    intro: "L'organisation du travail correspond à la manière dont les activités professionnelles sont structurées et réparties au sein d'une équipe.",
    intro2: "Elle comprend notamment :",
    points: [
      "La répartition des tâches",
      "La définition des responsabilités",
      "La planification du temps de travail"
    ],
    intro3: "Une organisation efficace doit permettre de :",
    points2: [
      "Garantir la continuité de l'accompagnement des usagers",
      "Assurer une répartition équitable de la charge de travail",
      "Favoriser la coopération entre professionnels"
    ],
    narration: "L'organisation du travail structure les activités, définit les responsabilités et planifie le temps. Elle doit garantir continuité, équité et coopération."
  },

  // ===== SECTION 6 — Cycles et roulements =====
  // SLIDE 59
  { type: 'section', title: "6. Les cycles de travail et les roulements",
    narration: "Les cycles de travail et les roulements." },

  // SLIDE 60 — Cycles de travail
  {
    type: 'content',
    title: "Les cycles de travail",
    intro: "Dans le secteur social et médico-social, l'activité se déroule souvent sur des plages horaires étendues.",
    intro2: "Les cycles de travail permettent d'organiser la répartition du temps de travail sur plusieurs semaines. Ils doivent tenir compte :",
    points: [
      "Des obligations légales",
      "Des besoins du service",
      "Du bien-être des professionnels"
    ],
    narration: "Les cycles de travail répartissent le temps de travail sur plusieurs semaines en tenant compte des obligations légales, des besoins du service et du bien-être."
  },

  // SLIDE 61 — Roulements
  {
    type: 'content',
    title: "Les roulements",
    intro: "Les roulements correspondent à la rotation des professionnels sur différents postes ou horaires.",
    intro2: "Ils permettent notamment :",
    points: [
      "D'assurer la continuité de service",
      "De répartir les contraintes horaires",
      "D'éviter la surcharge de certains professionnels"
    ],
    narration: "Les roulements assurent la continuité de service, répartissent les contraintes et évitent la surcharge."
  },

  // ===== SECTION 7 — Astreintes =====
  // SLIDE 62
  { type: 'section', title: "7. La gestion des astreintes et des permanences",
    narration: "La gestion des astreintes et des permanences." },

  // SLIDE 63 — Astreintes
  {
    type: 'content',
    title: "Les astreintes",
    intro: "Dans certains services, des dispositifs d'astreinte peuvent être mis en place afin de garantir une réponse rapide en cas de situation urgente.",
    highlight: "L'astreinte correspond à une période durant laquelle le professionnel doit rester disponible pour intervenir si nécessaire.",
    narration: "L'astreinte est une période de disponibilité pour intervenir en cas de situation urgente."
  },

  // ===== SECTION 8 — Outils plannings =====
  // SLIDE 64
  { type: 'section', title: "8. Les outils de gestion des plannings",
    narration: "Les outils de gestion des plannings." },

  // SLIDE 65 — Outils de planification
  {
    type: 'content',
    title: "Les outils de gestion du temps",
    intro: "Plusieurs outils peuvent être utilisés pour gérer les plannings :",
    points: [
      "Logiciels de planification",
      "Tableaux de suivi",
      "Calendriers partagés"
    ],
    intro2: "Ces outils permettent de :",
    points2: [
      "Visualiser les disponibilités des professionnels",
      "Anticiper les besoins du service",
      "Faciliter les ajustements"
    ],
    narration: "Les outils de planification : logiciels, tableaux de suivi, calendriers partagés. Ils permettent de visualiser, anticiper et ajuster."
  },

  // ===== SECTION 9 — Congés et absentéisme =====
  // SLIDE 66
  { type: 'section', title: "9. L'anticipation des congés et la gestion de l'absentéisme",
    narration: "L'anticipation des congés et la gestion de l'absentéisme." },

  // SLIDE 67 — Congés
  {
    type: 'content',
    title: "L'anticipation des congés",
    intro: "Une gestion efficace des plannings suppose d'anticiper les périodes d'absence.",
    intro2: "Le responsable d'unité doit notamment :",
    points: [
      "Planifier les congés à l'avance",
      "S'assurer du maintien d'un effectif suffisant",
      "Organiser les remplacements si nécessaire"
    ],
    narration: "L'anticipation des congés implique de planifier à l'avance, maintenir un effectif suffisant et organiser les remplacements."
  },

  // SLIDE 68 — Absentéisme
  {
    type: 'content',
    title: "La gestion de l'absentéisme",
    intro: "L'absentéisme peut avoir des conséquences importantes sur l'organisation du travail.",
    intro2: "Il peut être lié à différents facteurs :",
    points: [
      "Problèmes de santé",
      "Difficultés organisationnelles",
      "Conditions de travail"
    ],
    highlight: "Le responsable d'unité doit analyser les causes de l'absentéisme afin de mettre en place des actions adaptées.",
    narration: "L'absentéisme peut être lié à la santé, aux difficultés organisationnelles ou aux conditions de travail. Il faut en analyser les causes."
  },

  // SLIDE 69 — Remplacements
  {
    type: 'content',
    title: "Les remplacements",
    intro: "En cas d'absence, il peut être nécessaire de mettre en place des solutions de remplacement :",
    points: [
      "La réorganisation du planning",
      "Le recours à des professionnels remplaçants",
      "L'ajustement temporaire des activités"
    ],
    narration: "Les solutions de remplacement : réorganisation du planning, professionnels remplaçants ou ajustement temporaire des activités."
  },

  // ===== SECTION 10 — La délégation =====
  // SLIDE 70
  { type: 'section', title: "10. Les principes et les étapes de la délégation",
    narration: "Les principes et les étapes de la délégation." },

  // SLIDE 71 — Définition délégation
  {
    type: 'content',
    title: "La délégation",
    intro: "La délégation correspond au processus par lequel un responsable confie certaines tâches ou responsabilités à un membre de l'équipe.",
    intro2: "La délégation permet notamment :",
    points: [
      "De développer les compétences des professionnels",
      "De renforcer leur autonomie",
      "D'améliorer l'efficacité de l'organisation"
    ],
    narration: "La délégation confie des tâches à l'équipe. Elle développe les compétences, renforce l'autonomie et améliore l'efficacité."
  },

  // SLIDE 72 — Étapes de la délégation
  {
    type: 'grid',
    title: "Les étapes de la délégation",
    items: [
      { num: "1", label: "Définition de la mission : objectifs, résultats attendus, délais" },
      { num: "2", label: "Clarification des responsabilités : limites de la délégation" },
      { num: "3", label: "Suivi et accompagnement : disponibilité du responsable" },
      { num: "4", label: "Évaluation : analyse des résultats et difficultés rencontrées" }
    ],
    narration: "Les quatre étapes : définir la mission, clarifier les responsabilités, assurer le suivi et évaluer les résultats."
  },

  // SLIDE 73 — Limites de la délégation
  {
    type: 'content',
    title: "Les limites de la délégation",
    intro: "Certaines responsabilités ne peuvent pas être déléguées, notamment celles liées à la responsabilité hiérarchique du cadre.",
    highlight: "Le responsable d'unité conserve la responsabilité finale des décisions prises.",
    narration: "Certaines responsabilités hiérarchiques ne peuvent pas être déléguées. Le responsable conserve la responsabilité finale."
  },

  // SLIDE 74 — Conclusion Chapitre 2
  {
    type: 'content',
    title: "Conclusion — Chapitre 2",
    intro: "Coordonner l'organisation du travail constitue une compétence essentielle du responsable d'unité.",
    intro2: "Cette mission implique de :",
    points: [
      "Structurer l'organisation du service",
      "Adapter les plannings aux besoins des usagers",
      "Soutenir une approche inclusive du management",
      "Développer l'autonomie des professionnels à travers la délégation"
    ],
    highlight: "Ces compétences contribuent à améliorer la qualité du fonctionnement du service et le bien-être des équipes professionnelles.",
    narration: "L'organisation du travail implique de structurer le service, adapter les plannings, être inclusif et déléguer. Cela améliore qualité et bien-être."
  },

  // =============================
  // CHAPITRE 3 — Obligations réglementaires
  // =============================

  // SLIDE 75 — Transition Chapitre 3
  { type: 'section', title: "Chapitre 3", subtitle: "Mettre en oeuvre les obligations réglementaires",
    narration: "Chapitre 3 : Mettre en oeuvre les obligations réglementaires." },

  // SLIDE 76 — Introduction Chapitre 3
  {
    type: 'content',
    title: "Introduction — Chapitre 3",
    intro: "Dans le secteur social et médico-social, le responsable d'unité doit assurer la gestion des ressources humaines dans le respect du cadre juridique et réglementaire.",
    intro2: "Le respect de ces obligations permet de :",
    points: [
      "Sécuriser les relations de travail",
      "Prévenir les conflits professionnels",
      "Garantir la protection des salariés",
      "Assurer la conformité de l'établissement avec les obligations légales"
    ],
    highlight: "Le responsable d'unité ne se substitue pas à la direction ou au service RH, mais il joue un rôle important dans l'application concrète des règles au sein du service.",
    narration: "Le responsable d'unité doit respecter le cadre juridique pour sécuriser les relations de travail, prévenir les conflits et protéger les salariés."
  },

  // SLIDE 77 — Objectifs pédagogiques Chap 3
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 3",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Comprendre les principales règles du droit du travail applicables dans le secteur",
      "Appliquer les dispositions des conventions collectives du secteur",
      "Veiller au respect des obligations en matière de santé et de sécurité",
      "Prévenir les risques professionnels au sein de l'équipe"
    ],
    narration: "Objectifs : comprendre le droit du travail, appliquer les conventions collectives, veiller à la santé-sécurité et prévenir les risques."
  },

  // SLIDE 78 — Sommaire détaillé Chapitre 3
  {
    type: 'grid',
    title: "Chapitre 3 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les fondements du droit du travail" },
      { num: "2", label: "Le contrat de travail et le lien de subordination" },
      { num: "3", label: "Les différents types de contrats" },
      { num: "4", label: "La durée du travail et le temps de travail" },
      { num: "5", label: "Les heures supplémentaires et le repos" },
      { num: "6", label: "Le pouvoir disciplinaire de l'employeur" },
      { num: "7", label: "Les conventions collectives (CCN 66, CCN 51)" },
      { num: "8", label: "Les accords de branche" },
      { num: "9", label: "La prévention des risques professionnels" },
      { num: "10", label: "Le DUERP" },
      { num: "11", label: "La prévention des RPS" },
      { num: "12", label: "Le rôle du CSE" }
    ],
    narration: "Le Chapitre 3 couvre 12 thèmes : droit du travail, contrats, durée du travail, discipline, conventions collectives, accords de branche, risques, DUERP, RPS et CSE."
  },

  // ===== SECTION 1 — Droit du travail =====
  // SLIDE 79
  { type: 'section', title: "1. Les fondements du droit du travail",
    narration: "Les fondements du droit du travail." },

  // SLIDE 80 — Définition droit du travail
  {
    type: 'content',
    title: "Définition du droit du travail",
    intro: "Le droit du travail correspond à l'ensemble des règles juridiques qui encadrent les relations entre employeurs et salariés.",
    intro2: "Il définit notamment :",
    points: [
      "Les conditions d'embauche",
      "Les droits et obligations des salariés",
      "L'organisation du temps de travail",
      "Les procédures disciplinaires"
    ],
    highlight: "Dans le secteur social et médico-social, ces règles sont complétées par les conventions collectives et les accords de branche.",
    narration: "Le droit du travail encadre les relations employeur-salarié : embauche, droits, temps de travail et discipline. Le secteur social y ajoute ses conventions collectives."
  },

  // ===== SECTION 2 — Le contrat de travail =====
  // SLIDE 81
  { type: 'section', title: "2. Le contrat de travail et le lien de subordination",
    narration: "Le contrat de travail et le lien de subordination." },

  // SLIDE 82 — Contrat de travail
  {
    type: 'content',
    title: "Le contrat de travail",
    intro: "Le contrat de travail constitue le document juridique qui formalise la relation entre l'employeur et le salarié.",
    intro2: "Il repose sur trois éléments essentiels :",
    points: [
      "La réalisation d'un travail",
      "La rémunération",
      "Le lien de subordination"
    ],
    highlight: "Le lien de subordination correspond à la situation dans laquelle le salarié exerce son activité sous l'autorité de l'employeur ou de son représentant.",
    narration: "Le contrat de travail repose sur trois éléments : travail, rémunération et lien de subordination."
  },

  // ===== SECTION 3 — Types de contrats =====
  // SLIDE 83
  { type: 'section', title: "3. Les différents types de contrats dans le secteur social",
    narration: "Les différents types de contrats dans le secteur social." },

  // SLIDE 84 — Types de contrats
  {
    type: 'grid',
    title: "Les différents types de contrats",
    items: [
      { num: "CDI", label: "Contrat à durée indéterminée — forme normale de la relation de travail, sans date de fin." },
      { num: "CDD", label: "Contrat à durée déterminée — pour un besoin temporaire : remplacement ou accroissement d'activité." },
      { num: "App", label: "Contrat d'apprentissage — formation en alternance pour les jeunes." },
      { num: "Pro", label: "Contrat de professionnalisation — formation qualifiante en alternance." }
    ],
    narration: "Les principaux contrats : CDI (forme normale), CDD (besoin temporaire), apprentissage et professionnalisation (alternance)."
  },

  // ===== SECTION 4 — Durée du travail =====
  // SLIDE 85
  { type: 'section', title: "4. La durée du travail et l'organisation du temps de travail",
    narration: "La durée du travail et l'organisation du temps de travail." },

  // SLIDE 86 — Durée légale
  {
    type: 'content',
    title: "La durée légale du travail",
    intro: "En France, la durée légale du travail est fixée à 35 heures par semaine pour les salariés à temps plein.",
    intro2: "Dans le secteur social et médico-social, l'organisation peut inclure :",
    points: [
      "Travail de nuit",
      "Travail le week-end",
      "Horaires décalés"
    ],
    intro3: "Le responsable d'unité doit veiller au respect des règles relatives :",
    points2: [
      "Au temps de repos",
      "Aux amplitudes horaires",
      "Aux durées maximales de travail"
    ],
    narration: "La durée légale est de 35 heures. Le secteur social inclut travail de nuit, week-end et horaires décalés. Le responsable veille au respect des repos et amplitudes."
  },

  // ===== SECTION 5 — Heures supplémentaires =====
  // SLIDE 87
  { type: 'section', title: "5. Les heures supplémentaires et le temps de repos",
    narration: "Les heures supplémentaires et le temps de repos." },

  // SLIDE 88 — Heures sup
  {
    type: 'content',
    title: "Les heures supplémentaires",
    intro: "Les heures supplémentaires correspondent aux heures de travail effectuées au-delà de la durée légale.",
    intro2: "Elles donnent lieu :",
    points: [
      "À une majoration de salaire",
      "Ou à un repos compensateur"
    ],
    highlight: "Le responsable d'unité doit être vigilant dans l'organisation du travail afin de limiter les situations d'heures supplémentaires non anticipées.",
    narration: "Les heures supplémentaires donnent lieu à majoration ou repos compensateur. Le responsable doit limiter les heures non anticipées."
  },

  // ===== SECTION 6 — Pouvoir disciplinaire =====
  // SLIDE 89
  { type: 'section', title: "6. Le pouvoir disciplinaire de l'employeur",
    narration: "Le pouvoir disciplinaire de l'employeur." },

  // SLIDE 90 — Pouvoir disciplinaire
  {
    type: 'content',
    title: "Le pouvoir disciplinaire",
    intro: "L'employeur dispose d'un pouvoir disciplinaire lui permettant de sanctionner les comportements fautifs des salariés.",
    intro2: "Les sanctions peuvent être :",
    points: [
      "Un avertissement",
      "Un blâme",
      "Une mise à pied disciplinaire",
      "Un licenciement disciplinaire"
    ],
    highlight: "Le responsable d'unité peut être amené à signaler certaines situations à la direction ou au service des ressources humaines.",
    narration: "Le pouvoir disciplinaire permet de sanctionner : avertissement, blâme, mise à pied ou licenciement. Le responsable signale les situations à la direction."
  },

  // ===== SECTION 7 — Conventions collectives =====
  // SLIDE 91
  { type: 'section', title: "7. Les conventions collectives du secteur (CCN 66, CCN 51)",
    narration: "Les conventions collectives du secteur." },

  // SLIDE 92 — Définition CC
  {
    type: 'content',
    title: "Les conventions collectives",
    intro: "Les conventions collectives sont des accords négociés entre les organisations d'employeurs et les organisations syndicales.",
    highlight: "Elles précisent les règles applicables dans un secteur d'activité donné. Dans le social et médico-social, deux conventions sont particulièrement importantes.",
    narration: "Les conventions collectives sont des accords entre employeurs et syndicats. Deux sont essentielles dans le social : la CCN 66 et la CCN 51."
  },

  // SLIDE 93 — CCN 66
  {
    type: 'content',
    title: "La Convention Collective 66 (CCN 66)",
    intro: "La convention collective nationale du 15 mars 1966 concerne de nombreux établissements du secteur social et médico-social.",
    intro2: "Elle s'applique notamment :",
    points: [
      "Aux établissements pour personnes handicapées",
      "Aux structures de protection de l'enfance"
    ],
    intro3: "Elle prévoit des dispositions spécifiques concernant :",
    points2: [
      "La classification des emplois",
      "Les conditions de travail",
      "La rémunération"
    ],
    narration: "La CCN 66 concerne les établissements pour personnes handicapées et la protection de l'enfance. Elle régit classification, conditions de travail et rémunération."
  },

  // SLIDE 94 — CCN 51
  {
    type: 'content',
    title: "La Convention Collective 51 (CCN 51)",
    intro: "La convention collective du 31 octobre 1951 concerne principalement les établissements privés à but non lucratif du secteur sanitaire et médico-social.",
    intro2: "Elle prévoit des dispositions relatives :",
    points: [
      "Aux carrières professionnelles",
      "Aux conditions de travail",
      "Aux évolutions salariales"
    ],
    narration: "La CCN 51 concerne les établissements privés non lucratifs du sanitaire et médico-social. Elle régit carrières, conditions de travail et évolutions salariales."
  },

  // ===== SECTION 8 — Accords de branche =====
  // SLIDE 95
  { type: 'section', title: "8. Les accords de branche",
    narration: "Les accords de branche." },

  // SLIDE 96 — Accords de branche
  {
    type: 'content',
    title: "Les accords de branche",
    intro: "Les accords de branche peuvent compléter les conventions collectives.",
    intro2: "Ils portent souvent sur :",
    points: [
      "La formation professionnelle",
      "L'égalité professionnelle",
      "L'organisation du temps de travail"
    ],
    highlight: "Le responsable d'unité doit connaître les principales dispositions applicables dans son établissement.",
    narration: "Les accords de branche complètent les conventions collectives et portent sur la formation, l'égalité et le temps de travail."
  },

  // ===== SECTION 9 — Prévention des risques =====
  // SLIDE 97
  { type: 'section', title: "9. La prévention des risques professionnels",
    narration: "La prévention des risques professionnels." },

  // SLIDE 98 — Obligations employeur
  {
    type: 'content',
    title: "Les obligations de l'employeur en matière de santé et sécurité",
    intro: "L'employeur a l'obligation de garantir la santé et la sécurité des salariés.",
    intro2: "Cette obligation repose sur plusieurs principes :",
    points: [
      "Prévenir les risques professionnels",
      "Informer les salariés",
      "Mettre en place des mesures de protection"
    ],
    highlight: "Le responsable d'unité participe à la mise en oeuvre de ces actions au niveau du service.",
    narration: "L'employeur doit garantir la santé et la sécurité : prévenir les risques, informer et protéger. Le responsable d'unité y participe."
  },

  // ===== SECTION 10 — DUERP =====
  // SLIDE 99
  { type: 'section', title: "10. Le Document Unique d'Évaluation des Risques Professionnels (DUERP)",
    narration: "Le Document Unique d'Évaluation des Risques Professionnels." },

  // SLIDE 100 — DUERP
  {
    type: 'content',
    title: "Le DUERP",
    intro: "Le DUERP est un document obligatoire dans toutes les organisations.",
    intro2: "Il consiste à identifier et analyser les risques auxquels les salariés peuvent être exposés. Ces risques peuvent être :",
    points: [
      "Physiques",
      "Organisationnels",
      "Psychosociaux"
    ],
    highlight: "Le DUERP doit être régulièrement mis à jour.",
    narration: "Le DUERP identifie et analyse les risques physiques, organisationnels et psychosociaux. Il est obligatoire et doit être régulièrement mis à jour."
  },

  // ===== SECTION 11 — RPS =====
  // SLIDE 101
  { type: 'section', title: "11. La prévention des risques psychosociaux (RPS)",
    narration: "La prévention des risques psychosociaux." },

  // SLIDE 102 — RPS définition
  {
    type: 'content',
    title: "Les risques psychosociaux (RPS)",
    intro: "Les risques psychosociaux correspondent aux situations de travail susceptibles de porter atteinte à la santé mentale ou physique des salariés.",
    intro2: "Ils peuvent être liés à :",
    points: [
      "La charge de travail",
      "Les tensions relationnelles",
      "Le manque de reconnaissance",
      "L'exposition à des situations difficiles"
    ],
    highlight: "Dans le secteur social et médico-social, ces risques peuvent être particulièrement présents en raison de la charge émotionnelle du travail.",
    narration: "Les RPS portent atteinte à la santé des salariés : charge de travail, tensions, manque de reconnaissance. Le secteur social est particulièrement exposé."
  },

  // SLIDE 103 — Prévention RPS
  {
    type: 'content',
    title: "La prévention des RPS",
    intro: "La prévention des risques psychosociaux repose sur plusieurs actions :",
    points: [
      "Améliorer l'organisation du travail",
      "Favoriser la communication au sein de l'équipe",
      "Soutenir les professionnels dans les situations difficiles"
    ],
    intro2: "Le responsable d'unité peut notamment mettre en place :",
    points2: [
      "Des temps d'échange",
      "Des espaces d'analyse de pratiques",
      "Des actions de soutien aux équipes"
    ],
    narration: "La prévention des RPS passe par l'amélioration de l'organisation, la communication et le soutien. Le responsable peut proposer temps d'échange, analyse de pratiques et soutien."
  },

  // ===== SECTION 12 — CSE =====
  // SLIDE 104
  { type: 'section', title: "12. Le rôle du Comité Social et Économique (CSE)",
    narration: "Le rôle du Comité Social et Économique." },

  // SLIDE 105 — CSE
  {
    type: 'content',
    title: "Le Comité Social et Économique (CSE)",
    intro: "Le CSE est une instance représentative du personnel. Il a pour mission de représenter les salariés auprès de l'employeur.",
    intro2: "Ses principales missions sont :",
    points: [
      "Veiller au respect des droits des salariés",
      "Contribuer à l'amélioration des conditions de travail",
      "Participer à la prévention des risques professionnels"
    ],
    highlight: "Le responsable d'unité peut être amené à travailler en lien avec le CSE sur certaines questions relatives à l'organisation du travail.",
    narration: "Le CSE représente les salariés : droits, conditions de travail et prévention des risques. Le responsable d'unité collabore avec le CSE."
  },

  // SLIDE 106 — Conclusion Chapitre 3
  {
    type: 'content',
    title: "Conclusion — Chapitre 3",
    intro: "La mise en oeuvre des obligations réglementaires constitue une dimension essentielle du management.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Appliquer les règles du droit du travail",
      "Respecter les dispositions des conventions collectives",
      "Prévenir les risques professionnels"
    ],
    highlight: "Ces compétences permettent de sécuriser les relations de travail et de garantir un environnement professionnel respectueux des droits des salariés.",
    narration: "Les obligations réglementaires impliquent de maîtriser le droit du travail, les conventions collectives et la prévention des risques pour sécuriser les relations de travail."
  },

  // =============================
  // CHAPITRE 4 — Développer les compétences
  // =============================

  // SLIDE 107 — Transition Chapitre 4
  { type: 'section', title: "Chapitre 4", subtitle: "Développer les compétences individuelles et collectives",
    narration: "Chapitre 4 : Développer les compétences individuelles et collectives." },

  // SLIDE 108 — Introduction Chapitre 4
  {
    type: 'content',
    title: "Introduction — Chapitre 4",
    intro: "La qualité de l'accompagnement des usagers repose largement sur les compétences des professionnels. Les métiers de l'intervention sociale évoluent sous l'effet de :",
    points: [
      "L'évolution des politiques publiques",
      "Les transformations des besoins des publics",
      "Les innovations dans les pratiques professionnelles",
      "Les évolutions réglementaires"
    ],
    highlight: "Le développement des compétences constitue un levier essentiel pour améliorer les pratiques, soutenir l'engagement et renforcer la qualité du service.",
    narration: "Les compétences des professionnels sont essentielles. Le développement des compétences est un levier pour améliorer pratiques, engagement et qualité."
  },

  // SLIDE 109 — Objectifs pédagogiques Chap 4
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 4",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Identifier les compétences nécessaires au fonctionnement du service",
      "Accompagner les professionnels dans leur évolution professionnelle",
      "Conduire des entretiens professionnels et d'évaluation",
      "Participer à l'élaboration du plan de développement des compétences",
      "Soutenir les dynamiques d'apprentissage collectif"
    ],
    narration: "Objectifs : identifier les compétences, accompagner les évolutions, conduire les entretiens, élaborer le plan de développement et soutenir l'apprentissage collectif."
  },

  // SLIDE 110 — Sommaire détaillé Chapitre 4
  {
    type: 'grid',
    title: "Chapitre 4 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les enjeux du développement des compétences" },
      { num: "2", label: "La GPEC (Gestion Prévisionnelle des Emplois et Compétences)" },
      { num: "3", label: "L'analyse des compétences à l'échelle de l'unité" },
      { num: "4", label: "L'entretien professionnel" },
      { num: "5", label: "L'entretien annuel d'évaluation" },
      { num: "6", label: "L'identification des besoins en formation" },
      { num: "7", label: "Le plan de développement des compétences" },
      { num: "8", label: "Les dispositifs de formation professionnelle" },
      { num: "9", label: "Les groupes d'analyse des pratiques (GAP)" },
      { num: "10", label: "La supervision et les dispositifs de soutien" }
    ],
    narration: "Le Chapitre 4 couvre 10 thèmes : enjeux, GPEC, analyse des compétences, entretiens, besoins en formation, plan de développement, formation, GAP et supervision."
  },

  // ===== SECTION 1 — GPEC =====
  // SLIDE 111
  { type: 'section', title: "1. La Gestion Prévisionnelle des Emplois et des Compétences (GPEC)",
    narration: "La Gestion Prévisionnelle des Emplois et des Compétences." },

  // SLIDE 112 — Définition GPEC
  {
    type: 'content',
    title: "Définition de la GPEC",
    intro: "La GPEC correspond à une démarche visant à anticiper les évolutions des métiers et des compétences nécessaires au fonctionnement d'une organisation.",
    highlight: "Un processus permettant d'anticiper les besoins futurs en compétences et d'adapter les ressources humaines aux évolutions de l'environnement.",
    intro2: "Dans le secteur social, la GPEC vise notamment à :",
    points: [
      "Anticiper les évolutions des métiers",
      "Identifier les besoins en formation",
      "Accompagner les parcours professionnels"
    ],
    narration: "La GPEC anticipe les évolutions des métiers et adapte les ressources humaines. Elle identifie les besoins en formation et accompagne les parcours."
  },

  // SLIDE 113 — Enjeux GPEC
  {
    type: 'content',
    title: "Les enjeux de la GPEC",
    intro: "La GPEC permet de répondre à plusieurs enjeux :",
    points: [
      "L'évolution des métiers : nouveaux besoins des usagers, évolutions réglementaires, innovations",
      "Le renouvellement des professionnels : départs à la retraite, mobilités professionnelles",
      "L'adaptation des compétences : nouveaux enjeux du secteur"
    ],
    narration: "Les enjeux de la GPEC : évolution des métiers, renouvellement des professionnels et adaptation des compétences."
  },

  // SLIDE 114 — GPEC à l'échelle de l'unité
  {
    type: 'content',
    title: "La GPEC à l'échelle de l'unité",
    intro: "Même si la GPEC est souvent pilotée à l'échelle de l'établissement, le responsable d'unité peut contribuer à cette démarche.",
    intro2: "Il peut notamment :",
    points: [
      "Analyser les compétences présentes dans l'équipe",
      "Identifier les besoins en formation",
      "Repérer les potentiels professionnels"
    ],
    narration: "Le responsable d'unité contribue à la GPEC en analysant les compétences, identifiant les besoins et repérant les potentiels."
  },

  // ===== SECTION 2 — Entretien professionnel =====
  // SLIDE 115
  { type: 'section', title: "2. L'entretien professionnel : cadre réglementaire et objectifs",
    narration: "L'entretien professionnel." },

  // SLIDE 116 — Cadre réglementaire
  {
    type: 'content',
    title: "L'entretien professionnel — Cadre réglementaire",
    intro: "L'entretien professionnel est obligatoire pour tous les salariés. Il doit être organisé tous les deux ans.",
    highlight: "Cet entretien est consacré aux perspectives d'évolution professionnelle du salarié. Il ne doit pas être confondu avec l'entretien d'évaluation.",
    intro2: "L'entretien professionnel permet notamment de :",
    points: [
      "Faire le point sur le parcours professionnel du salarié",
      "Identifier ses besoins en formation",
      "Envisager ses perspectives d'évolution"
    ],
    narration: "L'entretien professionnel est obligatoire tous les deux ans. Il porte sur les perspectives d'évolution, pas sur l'évaluation de l'activité."
  },

  // SLIDE 117 — Déroulement entretien pro
  {
    type: 'grid',
    title: "Les étapes de l'entretien professionnel",
    items: [
      { num: "1", label: "Préparation de l'entretien" },
      { num: "2", label: "Échange sur le parcours professionnel" },
      { num: "3", label: "Identification des compétences et des besoins" },
      { num: "4", label: "Définition d'actions de développement professionnel" }
    ],
    narration: "Les quatre étapes : préparation, échange sur le parcours, identification des besoins et définition d'actions de développement."
  },

  // ===== SECTION 3 — Entretien d'évaluation =====
  // SLIDE 118
  { type: 'section', title: "3. L'entretien annuel d'évaluation",
    narration: "L'entretien annuel d'évaluation." },

  // SLIDE 119 — Entretien d'évaluation
  {
    type: 'content',
    title: "L'entretien annuel d'évaluation",
    intro: "Contrairement à l'entretien professionnel, l'entretien d'évaluation porte sur l'activité du salarié au cours de l'année écoulée.",
    intro2: "Il permet notamment de :",
    points: [
      "Analyser les résultats obtenus",
      "Identifier les points forts et les axes d'amélioration",
      "Fixer des objectifs pour l'année suivante"
    ],
    narration: "L'entretien d'évaluation porte sur l'activité de l'année : résultats, points forts, axes d'amélioration et objectifs."
  },

  // SLIDE 120 — Comparaison entretiens
  {
    type: 'comparison',
    title: "Entretien professionnel vs Entretien d'évaluation",
    wrong: [
      { label: "Évaluation : porte sur l'activité passée", correct: "Professionnel : porte sur les perspectives d'évolution" },
      { label: "Évaluation : résultats et objectifs", correct: "Professionnel : parcours et besoins en formation" },
      { label: "Évaluation : non obligatoire", correct: "Professionnel : obligatoire tous les 2 ans" },
      { label: "Évaluation : performance du salarié", correct: "Professionnel : développement de carrière" }
    ],
    highlight: "Les deux entretiens se complètent. Ils constituent un moment privilégié d'échange pour valoriser le travail, soutenir la motivation et accompagner les évolutions.",
    narration: "L'entretien d'évaluation porte sur l'activité passée, l'entretien professionnel sur les perspectives. Les deux se complètent."
  },

  // ===== SECTION 4 — Formation professionnelle =====
  // SLIDE 121
  { type: 'section', title: "4. La formation professionnelle",
    narration: "La formation professionnelle." },

  // SLIDE 122 — Définition formation
  {
    type: 'content',
    title: "La formation professionnelle",
    intro: "La formation professionnelle vise à permettre aux salariés d'acquérir ou de développer des compétences nécessaires à l'exercice de leur activité.",
    highlight: "Dans le secteur social et médico-social, la formation constitue un levier essentiel pour améliorer la qualité des pratiques professionnelles.",
    narration: "La formation professionnelle permet d'acquérir des compétences. Dans le social, c'est un levier essentiel pour la qualité des pratiques."
  },

  // SLIDE 123 — Recueil besoins
  {
    type: 'content',
    title: "Le recueil des besoins en formation",
    intro: "Le responsable d'unité joue un rôle important dans l'identification des besoins en formation.",
    intro2: "Ces besoins peuvent être identifiés à partir de :",
    points: [
      "Les entretiens professionnels",
      "L'analyse des pratiques professionnelles",
      "L'évolution des missions du service"
    ],
    narration: "Les besoins en formation sont identifiés via les entretiens professionnels, l'analyse des pratiques et l'évolution des missions."
  },

  // SLIDE 124 — Plan de développement
  {
    type: 'content',
    title: "Le plan de développement des compétences",
    intro: "Le plan de développement des compétences regroupe l'ensemble des actions de formation prévues par l'organisation.",
    intro2: "Il peut inclure :",
    points: [
      "Des formations techniques",
      "Des formations réglementaires",
      "Des formations liées au développement des pratiques professionnelles"
    ],
    highlight: "Le responsable d'unité peut contribuer à l'élaboration de ce plan en proposant des actions adaptées aux besoins du service.",
    narration: "Le plan de développement des compétences regroupe les formations techniques, réglementaires et de développement des pratiques."
  },

  // ===== SECTION 5 — Analyse des pratiques =====
  // SLIDE 125
  { type: 'section', title: "5. Les groupes d'analyse des pratiques professionnelles (GAP)",
    narration: "Les groupes d'analyse des pratiques professionnelles." },

  // SLIDE 126 — GAP
  {
    type: 'content',
    title: "L'analyse des pratiques professionnelles",
    intro: "L'analyse des pratiques professionnelles consiste à créer un espace d'échange permettant aux professionnels de réfléchir collectivement à leurs pratiques.",
    intro2: "Elle vise à :",
    points: [
      "Améliorer la qualité des interventions",
      "Soutenir la réflexion éthique",
      "Prévenir l'isolement professionnel"
    ],
    narration: "L'analyse des pratiques crée un espace de réflexion collective pour améliorer les interventions, soutenir l'éthique et prévenir l'isolement."
  },

  // SLIDE 127 — GAP détail
  {
    type: 'content',
    title: "Les groupes d'analyse des pratiques (GAP)",
    intro: "Les GAP réunissent plusieurs professionnels autour de l'analyse de situations rencontrées dans leur travail.",
    intro2: "Ces groupes permettent notamment :",
    points: [
      "De partager les expériences professionnelles",
      "De confronter les points de vue",
      "De développer des solutions collectives"
    ],
    narration: "Les GAP permettent de partager les expériences, confronter les points de vue et développer des solutions collectives."
  },

  // ===== SECTION 6 — Supervision =====
  // SLIDE 128
  { type: 'section', title: "6. La supervision et les dispositifs de soutien professionnel",
    narration: "La supervision et les dispositifs de soutien professionnel." },

  // SLIDE 129 — Supervision
  {
    type: 'content',
    title: "La supervision",
    intro: "La supervision correspond à un dispositif d'accompagnement professionnel généralement animé par un intervenant extérieur.",
    intro2: "Elle peut contribuer à :",
    points: [
      "Soutenir les professionnels dans les situations difficiles",
      "Prévenir l'épuisement professionnel",
      "Améliorer la qualité de l'accompagnement"
    ],
    narration: "La supervision est animée par un intervenant extérieur. Elle soutient les professionnels, prévient l'épuisement et améliore la qualité."
  },

  // SLIDE 130 — Bénéfices apprentissage collectif
  {
    type: 'content',
    title: "Les bénéfices de l'apprentissage collectif",
    intro: "Les dispositifs d'analyse des pratiques contribuent à développer une culture professionnelle partagée.",
    intro2: "Ils permettent notamment :",
    points: [
      "De renforcer la cohésion de l'équipe",
      "D'améliorer la qualité des interventions",
      "De soutenir le développement des compétences collectives"
    ],
    narration: "L'apprentissage collectif renforce la cohésion, améliore les interventions et développe les compétences collectives."
  },

  // SLIDE 131 — Conclusion Chapitre 4
  {
    type: 'content',
    title: "Conclusion — Chapitre 4",
    intro: "Le développement des compétences constitue une dimension essentielle du management.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Identifier les compétences nécessaires au fonctionnement du service",
      "Accompagner les professionnels dans leur évolution",
      "Soutenir les dynamiques d'apprentissage collectif"
    ],
    highlight: "Ces actions contribuent à améliorer la qualité des pratiques professionnelles et à renforcer l'efficacité de l'équipe.",
    narration: "Le développement des compétences implique d'identifier les besoins, accompagner les évolutions et soutenir l'apprentissage collectif."
  },

  // =============================
  // CHAPITRE 5 — Procédures de recrutement
  // =============================

  // SLIDE 132 — Transition Chapitre 5
  { type: 'section', title: "Chapitre 5", subtitle: "Appliquer les procédures de recrutement",
    narration: "Chapitre 5 : Appliquer les procédures de recrutement." },

  // SLIDE 133 — Introduction Chapitre 5
  {
    type: 'content',
    title: "Introduction — Chapitre 5",
    intro: "Le recrutement constitue une étape déterminante pour le fonctionnement d'une unité. Le choix des professionnels influence la qualité de l'accompagnement, la dynamique collective et l'efficacité de l'organisation.",
    intro2: "Le recrutement implique l'analyse :",
    points: [
      "Des capacités relationnelles des candidats",
      "De leur posture professionnelle",
      "De leur aptitude à travailler en équipe pluridisciplinaire",
      "De leur adhésion aux valeurs du projet d'établissement"
    ],
    narration: "Le recrutement est déterminant. Il implique d'analyser les capacités relationnelles, la posture, l'aptitude au travail en équipe et l'adhésion aux valeurs."
  },

  // SLIDE 134 — Objectifs pédagogiques Chap 5
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 5",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Identifier les besoins en recrutement au sein de l'unité",
      "Formaliser les missions et les compétences attendues",
      "Participer au processus de sélection des candidats",
      "Conduire un entretien de recrutement structuré",
      "Organiser l'intégration des nouveaux professionnels",
      "Respecter les règles juridiques relatives au recrutement"
    ],
    narration: "Objectifs : identifier les besoins, formaliser les attentes, sélectionner, conduire les entretiens, intégrer et respecter le cadre légal."
  },

  // SLIDE 135 — Sommaire détaillé Chapitre 5
  {
    type: 'grid',
    title: "Chapitre 5 — Sommaire détaillé",
    items: [
      { num: "1", label: "L'identification du besoin de recrutement" },
      { num: "2", label: "La rédaction de la fiche de poste et du profil" },
      { num: "3", label: "Les stratégies de sourcing des candidats" },
      { num: "4", label: "Le tri et l'analyse des candidatures" },
      { num: "5", label: "La préparation de l'entretien de recrutement" },
      { num: "6", label: "Les techniques de questionnement" },
      { num: "7", label: "L'évaluation des compétences et de la posture" },
      { num: "8", label: "Le processus de décision" },
      { num: "9", label: "L'intégration des nouveaux professionnels" },
      { num: "10", label: "Le tutorat et la période d'essai" },
      { num: "11", label: "Le cadre légal et la non-discrimination" }
    ],
    narration: "Le Chapitre 5 couvre 11 thèmes : besoin de recrutement, fiche de poste, sourcing, tri, entretien, questionnement, évaluation, décision, intégration, tutorat et cadre légal."
  },

  // ===== SECTION 1 — Identification du besoin =====
  // SLIDE 136
  { type: 'section', title: "1. L'identification du besoin de recrutement",
    narration: "L'identification du besoin de recrutement." },

  // SLIDE 137 — Besoin de recrutement
  {
    type: 'content',
    title: "L'identification du besoin de recrutement",
    intro: "La première étape consiste à identifier précisément le besoin. Ce besoin peut être lié :",
    points: [
      "Au remplacement d'un salarié absent",
      "Au départ d'un professionnel",
      "Au développement de nouvelles activités",
      "À une évolution de l'organisation du service"
    ],
    intro2: "Le responsable d'unité doit déterminer :",
    points2: [
      "Les missions à assurer",
      "Les compétences nécessaires",
      "Le niveau d'expérience attendu"
    ],
    narration: "Le besoin de recrutement peut être lié à un remplacement, un départ, de nouvelles activités ou une réorganisation. Le responsable identifie missions, compétences et expérience."
  },

  // ===== SECTION 2 — Fiche de poste =====
  // SLIDE 138
  { type: 'section', title: "2. La rédaction de la fiche de poste et du profil de poste",
    narration: "La fiche de poste et le profil de poste." },

  // SLIDE 139 — Fiche de poste
  {
    type: 'content',
    title: "La fiche de poste",
    intro: "La fiche de poste décrit les principales caractéristiques d'un poste au sein d'une organisation.",
    intro2: "Elle précise notamment :",
    points: [
      "L'intitulé du poste",
      "Les missions principales",
      "Les responsabilités",
      "Les compétences requises",
      "Les conditions d'exercice"
    ],
    highlight: "La fiche de poste permet de clarifier les attentes de l'organisation et constitue un outil de référence pour le recrutement.",
    narration: "La fiche de poste précise l'intitulé, les missions, responsabilités, compétences et conditions d'exercice. C'est un outil de référence pour le recrutement."
  },

  // SLIDE 140 — Profil de poste
  {
    type: 'content',
    title: "Le profil de poste",
    intro: "Le profil de poste complète la fiche de poste. Il précise les caractéristiques attendues chez le candidat :",
    points: [
      "Formation",
      "Expérience professionnelle",
      "Compétences techniques",
      "Qualités relationnelles"
    ],
    intro2: "Dans le secteur social, certaines qualités sont particulièrement recherchées :",
    points2: [
      "Capacité d'écoute",
      "Aptitude à travailler en équipe",
      "Sens de l'éthique professionnelle"
    ],
    narration: "Le profil de poste précise formation, expérience, compétences techniques et qualités relationnelles attendues."
  },

  // ===== SECTION 3 — Sourcing =====
  // SLIDE 141
  { type: 'section', title: "3. Les stratégies de sourcing des candidats",
    narration: "Les stratégies de sourcing des candidats." },

  // SLIDE 142 — Sourcing
  {
    type: 'content',
    title: "Le sourcing des candidats",
    intro: "Le sourcing correspond à l'ensemble des actions visant à identifier des candidats potentiels.",
    intro2: "Il peut s'appuyer sur différents canaux :",
    points: [
      "Diffusion d'annonces",
      "Réseaux professionnels",
      "Plateformes spécialisées",
      "Candidatures spontanées"
    ],
    highlight: "Le responsable d'unité peut participer à la diffusion des offres et à la recherche de candidats.",
    narration: "Le sourcing identifie les candidats via annonces, réseaux professionnels, plateformes et candidatures spontanées."
  },

  // ===== SECTION 4 — Tri des candidatures =====
  // SLIDE 143
  { type: 'section', title: "4. Le tri et l'analyse des candidatures",
    narration: "Le tri et l'analyse des candidatures." },

  // SLIDE 144 — Tri
  {
    type: 'content',
    title: "Le tri des candidatures",
    intro: "Une fois les candidatures reçues, il est nécessaire d'effectuer une première sélection.",
    intro2: "Cette étape consiste à analyser :",
    points: [
      "Les diplômes",
      "L'expérience professionnelle",
      "Les compétences mentionnées dans le CV"
    ],
    highlight: "Les candidats correspondant le mieux aux critères du poste sont ensuite invités à un entretien.",
    narration: "Le tri analyse diplômes, expérience et compétences. Les meilleurs profils sont invités à un entretien."
  },

  // ===== SECTION 5 — Entretien de recrutement =====
  // SLIDE 145
  { type: 'section', title: "5. La conduite de l'entretien de recrutement",
    narration: "La conduite de l'entretien de recrutement." },

  // SLIDE 146 — Objectifs entretien
  {
    type: 'content',
    title: "Objectifs de l'entretien de recrutement",
    intro: "L'entretien de recrutement vise à :",
    points: [
      "Vérifier l'adéquation entre le profil du candidat et le poste",
      "Évaluer les compétences et les motivations du candidat",
      "Apprécier sa capacité à s'intégrer dans l'équipe"
    ],
    intro2: "Le responsable d'unité doit notamment :",
    points2: [
      "Analyser le CV du candidat",
      "Préparer des questions adaptées au poste",
      "Définir les critères d'évaluation"
    ],
    narration: "L'entretien vérifie l'adéquation profil-poste, évalue compétences et motivations, et apprécie la capacité d'intégration."
  },

  // ===== SECTION 6 — Techniques de questionnement =====
  // SLIDE 147
  { type: 'section', title: "6. Les techniques de questionnement lors de l'entretien",
    narration: "Les techniques de questionnement lors de l'entretien." },

  // SLIDE 148 — Questions ouvertes et situationnelles
  {
    type: 'content',
    title: "Les techniques de questionnement",
    intro: "Plusieurs techniques de questionnement peuvent être utilisées :",
    points: [
      "Questions ouvertes — permettent au candidat de développer sa réponse",
      "Questions situationnelles — analysent la réaction face à une situation professionnelle",
      "Questions comportementales — analysent les comportements passés"
    ],
    highlight: "Les comportements passés sont souvent révélateurs des comportements futurs.",
    narration: "Les techniques : questions ouvertes pour développer, situationnelles pour les réactions, comportementales pour les comportements passés."
  },

  // ===== SECTION 7 — Évaluation des candidats =====
  // SLIDE 149
  { type: 'section', title: "7. L'évaluation des compétences et de la posture professionnelle",
    narration: "L'évaluation des compétences et de la posture professionnelle." },

  // SLIDE 150 — Évaluation
  {
    type: 'content',
    title: "L'évaluation des candidats",
    intro: "L'évaluation doit prendre en compte plusieurs dimensions :",
    points: [
      "Les compétences techniques",
      "Les capacités relationnelles",
      "La motivation",
      "L'adéquation avec les valeurs de l'établissement"
    ],
    highlight: "Dans le secteur social, la dimension relationnelle est particulièrement importante.",
    narration: "L'évaluation prend en compte compétences techniques, relationnelles, motivation et adéquation aux valeurs. La dimension relationnelle est clé dans le social."
  },

  // ===== SECTION 8 — Intégration =====
  // SLIDE 151
  { type: 'section', title: "8. L'intégration des nouveaux professionnels",
    narration: "L'intégration des nouveaux professionnels." },

  // SLIDE 152 — Parcours d'intégration
  {
    type: 'content',
    title: "Le parcours d'intégration",
    intro: "L'intégration constitue une étape essentielle pour favoriser l'adaptation du nouveau professionnel.",
    intro2: "Le parcours d'intégration peut inclure :",
    points: [
      "La présentation de l'établissement",
      "La découverte de l'équipe",
      "La présentation des procédures internes"
    ],
    highlight: "Ce parcours permet au nouveau professionnel de comprendre le fonctionnement du service.",
    narration: "L'intégration est essentielle : présentation de l'établissement, découverte de l'équipe et présentation des procédures."
  },

  // ===== SECTION 9 — Tutorat =====
  // SLIDE 153
  { type: 'section', title: "9. Le tutorat et l'accompagnement pendant la période d'essai",
    narration: "Le tutorat et l'accompagnement pendant la période d'essai." },

  // SLIDE 154 — Tutorat
  {
    type: 'content',
    title: "Le tutorat",
    intro: "Le tutorat consiste à désigner un professionnel expérimenté chargé d'accompagner le nouveau salarié.",
    intro2: "Le tuteur peut notamment :",
    points: [
      "Répondre aux questions du nouvel arrivant",
      "Faciliter son intégration dans l'équipe",
      "Transmettre les pratiques professionnelles"
    ],
    narration: "Le tutorat désigne un professionnel expérimenté pour accompagner le nouveau salarié : réponses, intégration et transmission des pratiques."
  },

  // SLIDE 155 — Période d'essai
  {
    type: 'content',
    title: "Le suivi de la période d'essai",
    intro: "La période d'essai permet d'évaluer l'adéquation entre le professionnel et le poste.",
    intro2: "Le responsable d'unité doit :",
    points: [
      "Observer l'intégration du professionnel",
      "Identifier les éventuelles difficultés",
      "Organiser des temps d'échange"
    ],
    highlight: "Ces échanges permettent d'ajuster l'accompagnement si nécessaire.",
    narration: "La période d'essai évalue l'adéquation professionnel-poste. Le responsable observe, identifie les difficultés et organise des échanges."
  },

  // ===== SECTION 10 — Cadre légal =====
  // SLIDE 156
  { type: 'section', title: "10. Le cadre légal du recrutement et la non-discrimination",
    narration: "Le cadre légal du recrutement et la non-discrimination." },

  // SLIDE 157 — Non-discrimination
  {
    type: 'content',
    title: "Le principe de non-discrimination",
    intro: "La loi interdit toute discrimination dans le recrutement. Les décisions doivent être fondées uniquement sur des critères professionnels.",
    intro2: "Il est interdit de discriminer un candidat en raison :",
    points: [
      "De son origine",
      "De son sexe",
      "De son âge",
      "De son handicap",
      "De ses convictions religieuses ou politiques"
    ],
    narration: "La loi interdit la discrimination au recrutement. Les décisions doivent reposer uniquement sur des critères professionnels."
  },

  // SLIDE 158 — Questions interdites
  {
    type: 'comparison',
    title: "Questions autorisées vs Questions interdites en entretien",
    wrong: [
      { label: "Situation familiale (enfants, mariage)", correct: "Disponibilité et contraintes horaires" },
      { label: "Religion ou convictions personnelles", correct: "Adhésion aux valeurs du projet d'établissement" },
      { label: "État de santé ou grossesse", correct: "Aptitude au poste de travail" },
      { label: "Origine ethnique ou nationalité", correct: "Qualifications et expérience professionnelle" }
    ],
    highlight: "Le responsable d'unité doit veiller à respecter ces règles afin de garantir un recrutement équitable.",
    narration: "Certaines questions sont interdites en entretien : situation familiale, religion, santé, origine. Seuls les critères professionnels comptent."
  },

  // SLIDE 159 — Conclusion Chapitre 5
  {
    type: 'content',
    title: "Conclusion — Chapitre 5",
    intro: "Le recrutement constitue une étape stratégique dans la gestion des ressources humaines.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Identifier les besoins en recrutement",
      "Participer au processus de sélection des candidats",
      "Organiser l'intégration des nouveaux professionnels",
      "Respecter les règles juridiques applicables"
    ],
    highlight: "Une démarche de recrutement structurée contribue à renforcer la qualité des équipes et la cohérence du fonctionnement du service.",
    narration: "Le recrutement est stratégique : identifier les besoins, sélectionner, intégrer et respecter le cadre légal pour renforcer la qualité des équipes."
  },

  // =============================
  // CHAPITRE 6 — Communication managériale RH
  // =============================

  // SLIDE 160 — Transition Chapitre 6
  { type: 'section', title: "Chapitre 6", subtitle: "Communiquer dans le cadre du management RH",
    narration: "Chapitre 6 : Communiquer dans le cadre du management RH." },

  // SLIDE 161 — Introduction Chapitre 6
  {
    type: 'content',
    title: "Introduction — Chapitre 6",
    intro: "Dans une unité d'intervention sociale, la communication constitue un levier essentiel du management des ressources humaines.",
    intro2: "La communication managériale participe à :",
    points: [
      "La construction du sens du travail",
      "La mobilisation des professionnels",
      "La prévention des tensions au sein de l'équipe"
    ],
    highlight: "Le responsable d'unité doit développer des compétences spécifiques en communication afin de favoriser un climat de confiance et de coopération.",
    narration: "La communication est un levier essentiel : sens du travail, mobilisation et prévention des tensions. Le responsable doit développer ses compétences communicationnelles."
  },

  // SLIDE 162 — Enjeux communication
  {
    type: 'content',
    title: "Les enjeux de la communication interne",
    intro: "Dans le secteur social et médico-social, la qualité de la communication interne joue un rôle déterminant pour :",
    points: [
      "Maintenir la cohésion de l'équipe",
      "Soutenir l'engagement des professionnels",
      "Accompagner les évolutions organisationnelles"
    ],
    narration: "La communication interne maintient la cohésion, soutient l'engagement et accompagne les évolutions organisationnelles."
  },

  // SLIDE 163 — Objectifs pédagogiques Chap 6
  {
    type: 'content',
    title: "Objectifs pédagogiques — Chapitre 6",
    intro: "À l'issue de ce chapitre, le professionnel doit être capable de :",
    points: [
      "Adopter une posture de communication adaptée à sa fonction managériale",
      "Transmettre des décisions organisationnelles de manière claire et constructive",
      "Utiliser le feedback comme outil de développement professionnel",
      "Gérer les tensions et les conflits au sein de l'équipe"
    ],
    narration: "Objectifs : posture de communication, transmission des décisions, feedback et gestion des conflits."
  },

  // SLIDE 164 — Sommaire détaillé Chapitre 6
  {
    type: 'grid',
    title: "Chapitre 6 — Sommaire détaillé",
    items: [
      { num: "1", label: "Les principes de la communication managériale" },
      { num: "2", label: "Donner du sens au travail et mobiliser les équipes" },
      { num: "3", label: "La communication des décisions organisationnelles" },
      { num: "4", label: "Les techniques de communication interpersonnelle" },
      { num: "5", label: "Le feedback constructif comme outil de management" },
      { num: "6", label: "La reconnaissance et la valorisation" },
      { num: "7", label: "L'identification et l'analyse des conflits" },
      { num: "8", label: "Les stratégies de gestion des conflits" },
      { num: "9", label: "La médiation interne et la négociation" },
      { num: "10", label: "La prévention des tensions dans les équipes" }
    ],
    narration: "Le Chapitre 6 couvre 10 thèmes : communication managériale, sens du travail, décisions, techniques, feedback, reconnaissance, conflits, stratégies, médiation et prévention."
  },

  // ===== SECTION 1 — Communication managériale =====
  // SLIDE 165
  { type: 'section', title: "1. Les principes de la communication managériale",
    narration: "Les principes de la communication managériale." },

  // SLIDE 166 — Définition communication managériale
  {
    type: 'content',
    title: "La communication managériale",
    intro: "La communication managériale peut être définie comme :",
    highlight: "L'ensemble des échanges d'informations et de messages visant à organiser le travail, mobiliser les équipes et accompagner les décisions de l'organisation.",
    intro2: "Elle permet notamment :",
    points: [
      "De clarifier les objectifs du service",
      "De donner du sens aux actions menées",
      "De favoriser l'adhésion des professionnels aux décisions organisationnelles"
    ],
    narration: "La communication managériale organise le travail, mobilise les équipes et accompagne les décisions. Elle clarifie les objectifs et donne du sens."
  },

  // ===== SECTION 2 — Donner du sens =====
  // SLIDE 167
  { type: 'section', title: "2. Donner du sens au travail et mobiliser les équipes",
    narration: "Donner du sens au travail et mobiliser les équipes." },

  // SLIDE 168 — Sens du travail
  {
    type: 'content',
    title: "Donner du sens au travail",
    intro: "L'un des rôles essentiels du responsable d'unité consiste à aider les professionnels à comprendre le sens de leur activité.",
    intro2: "Dans le secteur social, le sens du travail est souvent lié :",
    points: [
      "Aux valeurs de solidarité",
      "À l'accompagnement des personnes vulnérables",
      "À l'utilité sociale des missions exercées"
    ],
    intro3: "Le responsable d'unité doit régulièrement rappeler :",
    points2: [
      "Les objectifs du service",
      "Les valeurs de l'établissement",
      "La finalité des actions menées"
    ],
    highlight: "Cette démarche contribue à renforcer l'engagement des professionnels.",
    narration: "Donner du sens implique de rappeler les objectifs, valeurs et finalité des actions. Cela renforce l'engagement."
  },

  // ===== SECTION 3 — Communication des décisions =====
  // SLIDE 169
  { type: 'section', title: "3. La communication des décisions organisationnelles",
    narration: "La communication des décisions organisationnelles." },

  // SLIDE 170 — Décisions difficiles
  {
    type: 'content',
    title: "La communication des décisions difficiles",
    intro: "Le responsable d'unité peut être amené à annoncer des décisions difficiles :",
    points: [
      "Une réorganisation du service",
      "Une modification des horaires",
      "Une décision disciplinaire"
    ],
    intro2: "Plusieurs principes guident cette communication :",
    points2: [
      "La transparence — expliquer les raisons de la décision",
      "La clarté — message compréhensible et précis",
      "Le respect des professionnels — cadre respectueux"
    ],
    narration: "Les décisions difficiles exigent transparence, clarté et respect. Il faut expliquer les raisons et être précis."
  },

  // ===== SECTION 4 — Feedback =====
  // SLIDE 171
  { type: 'section', title: "4. Le feedback constructif comme outil de management",
    narration: "Le feedback constructif comme outil de management." },

  // SLIDE 172 — Définition feedback
  {
    type: 'content',
    title: "Le feedback",
    intro: "Le feedback correspond au retour d'information qu'un responsable adresse à un professionnel à propos de son activité.",
    intro2: "Il peut concerner :",
    points: [
      "La qualité du travail réalisé",
      "Les comportements professionnels",
      "L'atteinte des objectifs"
    ],
    highlight: "Le feedback constitue un outil essentiel pour soutenir le développement des compétences.",
    narration: "Le feedback est un retour d'information sur le travail, les comportements ou l'atteinte des objectifs. C'est un outil de développement."
  },

  // SLIDE 173 — Fonctions du feedback
  {
    type: 'content',
    title: "Les fonctions du feedback",
    intro: "Le feedback remplit plusieurs fonctions dans le management :",
    points: [
      "La reconnaissance — valoriser le travail accompli, facteur de motivation",
      "L'amélioration des pratiques — identifier les axes de progression",
      "La régulation des comportements — ajuster les pratiques durablement"
    ],
    narration: "Le feedback remplit trois fonctions : reconnaissance, amélioration des pratiques et régulation des comportements."
  },

  // SLIDE 174 — Principes du feedback constructif
  {
    type: 'content',
    title: "Les principes du feedback constructif",
    intro: "Un feedback efficace doit respecter plusieurs principes :",
    points: [
      "La précision — porter sur des faits observables",
      "L'équilibre — associer éléments positifs et points d'amélioration",
      "Le dialogue — permettre un échange"
    ],
    narration: "Le feedback constructif est précis, équilibré et dialogué."
  },

  // SLIDE 175 — Méthode DESC
  {
    type: 'grid',
    title: "La méthode DESC — Feedback structuré",
    items: [
      { num: "D", label: "Décrire la situation — faits objectifs et observables" },
      { num: "E", label: "Exprimer les ressentis — impact sur l'équipe ou le service" },
      { num: "S", label: "Spécifier les attentes — comportement ou résultat souhaité" },
      { num: "C", label: "Conclure sur les actions — plan d'action concret" }
    ],
    narration: "La méthode DESC : Décrire la situation, Exprimer les ressentis, Spécifier les attentes, Conclure sur les actions."
  },

  // ===== SECTION 5 — Conflits =====
  // SLIDE 176
  { type: 'section', title: "5. L'identification et l'analyse des conflits",
    narration: "L'identification et l'analyse des conflits." },

  // SLIDE 177 — Définition conflit
  {
    type: 'content',
    title: "Définition du conflit",
    intro: "Un conflit peut être défini comme :",
    highlight: "Une situation d'opposition entre deux ou plusieurs personnes liées à des divergences d'intérêts, de valeurs ou de perceptions.",
    intro2: "Dans une unité d'intervention sociale, les conflits peuvent être liés :",
    points: [
      "À des divergences de pratiques professionnelles",
      "À des tensions relationnelles",
      "À des difficultés organisationnelles"
    ],
    narration: "Un conflit est une opposition liée à des divergences d'intérêts, de valeurs ou de perceptions. Il peut concerner les pratiques, les relations ou l'organisation."
  },

  // SLIDE 178 — Types de conflits
  {
    type: 'grid',
    title: "Les types de conflits",
    items: [
      { num: "V", label: "Conflits de valeurs — conceptions différentes du travail ou de l'accompagnement" },
      { num: "R", label: "Conflits relationnels — relations interpersonnelles entre professionnels" },
      { num: "O", label: "Conflits organisationnels — répartition des tâches ou contraintes de travail" }
    ],
    narration: "Trois types de conflits : de valeurs (conceptions différentes), relationnels (interpersonnels) et organisationnels (répartition des tâches)."
  },

  // ===== SECTION 6 — Stratégies de gestion =====
  // SLIDE 179
  { type: 'section', title: "6. Les stratégies de gestion des conflits",
    narration: "Les stratégies de gestion des conflits." },

  // SLIDE 180 — Stratégies
  {
    type: 'grid',
    title: "Les stratégies de gestion des conflits",
    items: [
      { num: "1", label: "L'évitement — éviter la confrontation. Pertinent parfois, mais ne résout pas toujours le problème." },
      { num: "2", label: "Le compromis — chaque partie fait une concession pour trouver une solution." },
      { num: "3", label: "La collaboration — recherche ensemble d'une solution satisfaisante pour chacun. Approche la plus constructive." }
    ],
    narration: "Les stratégies : évitement, compromis et collaboration. La collaboration est souvent la plus constructive."
  },

  // ===== SECTION 7 — Médiation et négociation =====
  // SLIDE 181
  { type: 'section', title: "7. La médiation interne et la négociation",
    narration: "La médiation interne et la négociation." },

  // SLIDE 182 — Médiation
  {
    type: 'content',
    title: "La médiation interne",
    intro: "La médiation consiste à faciliter le dialogue entre les personnes en conflit.",
    intro2: "Le responsable d'unité peut jouer un rôle de médiateur en :",
    points: [
      "Favorisant l'écoute des différents points de vue",
      "Clarifiant les malentendus",
      "Recherchant des solutions partagées"
    ],
    narration: "La médiation facilite le dialogue. Le responsable écoute, clarifie les malentendus et recherche des solutions partagées."
  },

  // SLIDE 183 — Négociation
  {
    type: 'content',
    title: "Les techniques de négociation",
    intro: "La négociation vise à trouver un accord entre plusieurs parties.",
    intro2: "Dans le management d'équipe, elle peut être utilisée pour :",
    points: [
      "Résoudre un conflit",
      "Trouver un compromis organisationnel",
      "Clarifier certaines responsabilités"
    ],
    intro3: "Une négociation efficace repose sur :",
    points2: [
      "L'écoute des intérêts de chacun",
      "La recherche d'une solution équilibrée",
      "La construction d'un accord durable"
    ],
    narration: "La négociation trouve un accord pour résoudre conflits, trouver des compromis et clarifier les responsabilités. Elle repose sur l'écoute et l'équilibre."
  },

  // SLIDE 184 — Conclusion Chapitre 6
  {
    type: 'content',
    title: "Conclusion — Chapitre 6",
    intro: "La communication constitue un levier essentiel du management des ressources humaines.",
    intro2: "Le responsable d'unité doit être capable de :",
    points: [
      "Transmettre les orientations de l'organisation",
      "Accompagner les professionnels dans leur activité",
      "Réguler les tensions au sein de l'équipe"
    ],
    highlight: "Une communication claire, respectueuse et structurée contribue à renforcer la cohésion des équipes et à améliorer la qualité du fonctionnement du service.",
    narration: "La communication managériale implique de transmettre, accompagner et réguler. Clarté, respect et structure renforcent la cohésion et la qualité."
  },

  // =============================
  // CONCLUSION DU BLOC 2
  // =============================

  // SLIDE 185 — Synthèse Bloc 2
  {
    type: 'content',
    title: "Conclusion du Bloc 2",
    intro: "Synthèse des compétences managériales du responsable d'unité :",
    points: [
      "Animer une équipe pluridisciplinaire et pluriprofessionnelle",
      "Coordonner l'organisation du travail dans une approche inclusive",
      "Mettre en oeuvre les obligations réglementaires",
      "Développer les compétences individuelles et collectives",
      "Appliquer les procédures de recrutement",
      "Communiquer dans le cadre du management RH"
    ],
    highlight: "Le management des ressources humaines est au coeur de la fonction de responsable d'unité. Il articule pilotage de l'activité et accompagnement des équipes.",
    narration: "Le Bloc 2 couvre les six compétences managériales essentielles du responsable d'unité dans le secteur social et médico-social."
  },

  // SLIDE 186 — Points clés Bloc 2
  {
    type: 'content',
    title: "Points clés du Bloc 2",
    points: [
      "Le management situationnel : adapter son style aux professionnels et aux situations",
      "La motivation : Maslow (hiérarchie des besoins) et Herzberg (facteurs d'hygiène et de motivation)",
      "L'organisation inclusive : diversité, handicap, égalité femmes-hommes",
      "Le droit du travail : contrats, conventions collectives (CCN 66, CCN 51), DUERP, RPS",
      "Le développement des compétences : GPEC, entretiens, GAP, supervision",
      "Le recrutement structuré : fiche de poste, entretien, non-discrimination",
      "La communication managériale : feedback DESC, médiation, négociation"
    ],
    narration: "Points clés : management situationnel, motivation, organisation inclusive, droit du travail, développement des compétences, recrutement et communication."
  },

  // SLIDE 187 — Félicitations
  {
    type: 'congratulations',
    title: "Bloc 2 terminé !",
    message: "Vous avez parcouru l'ensemble du Bloc 2 : Manager et gérer les ressources humaines.",
    details: [
      "6 chapitres couverts",
      "Animation d'équipe et leadership situationnel",
      "Organisation inclusive du travail",
      "Obligations réglementaires et conventions collectives",
      "Développement des compétences et GPEC",
      "Procédures de recrutement structurées",
      "Communication managériale et gestion des conflits"
    ],
    narration: "Félicitations ! Vous avez terminé le Bloc 2 du CAFERUIS. Rendez-vous dans le Quiz BC2 pour valider vos acquis !"
  }
];

const CAFBC2Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  const progressPercent = Math.round(((currentSlide + 1) / slides.length) * 100);

  useEffect(() => {
    markSlideViewed(ACTIVITY_ID, currentSlide, slides.length);
  }, [currentSlide]);

  const nextSlide = useCallback(() => { if (currentSlide < slides.length - 1) { stopAudio(); setCurrentSlide(c => c + 1); } }, [currentSlide]);
  const prevSlide = useCallback(() => { if (currentSlide > 0) { stopAudio(); setCurrentSlide(c => c - 1); } }, [currentSlide]);
  const goToSlide = useCallback((idx) => { stopAudio(); setCurrentSlide(idx); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nextSlide, prevSlide]);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setIsPlaying(false); setIsLoading(false); };

  const getSlideText = (s) => {
    if (s.narration) return s.narration;
    let t = s.title || '';
    if (s.intro) t += '. ' + s.intro;
    if (s.points) t += '. ' + s.points.join('. ');
    return t;
  };

  const speakText = async (t) => {
    stopAudio();
    try {
      setIsLoading(true); setIsPlaying(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: t, speed: 1.1 }) });
      if (!response.ok) throw new Error('TTS failed');
      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      setIsLoading(false);
      if (audioData) {
        const audioBlob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => { toast.error("Erreur de lecture audio"); setIsPlaying(false); };
        audioRef.current.play().catch(() => { toast.error("Impossible de lire l'audio"); setIsPlaying(false); });
      } else { toast.error("Pas de données audio reçues"); setIsPlaying(false); }
    } catch { toast.error("Erreur lors de la génération audio"); setIsPlaying(false); setIsLoading(false); }
  };

  useEffect(() => { return () => { if (audioRef.current) audioRef.current.pause(); }; }, []);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(30,64,175,0.55)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                <Users size={30} className="text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg tracking-wide">{slide.title}</h1>
              <p className="text-lg md:text-xl text-white/90 mb-4 max-w-xl leading-relaxed drop-shadow">{slide.subtitle}</p>
              <div className="flex gap-2 flex-wrap justify-center">
                {['6 chapitres', `${slides.length} slides`, 'Audio inclus'].map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)' }}>{tag}</span>
                ))}
              </div>
              <p className="mt-4 text-xs text-white/60 max-w-lg">Certificat d'Aptitude aux Fonctions d'Encadrement et de Responsable d'Unité d'Intervention Sociale</p>
            </div>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(30,64,175,0.65)' }} />
            <div className="relative z-10 flex flex-col h-full p-5 overflow-auto">
              <h2 className="text-lg font-bold text-white text-center mb-3 drop-shadow">{slide.title}</h2>
              <div className="flex-1 flex flex-col justify-center max-w-2xl mx-auto w-full">
                <div className="space-y-1.5">
                  {slide.sections.map((sec, i) => (
                    <div key={i} onClick={() => goToSlide(sec.targetSlide)} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: theme.secondary, color: 'white' }}>{sec.num}</div>
                      <span className="text-white text-[13px] font-medium leading-snug">{sec.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(30,64,175,0.6)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8">
              <Sparkles size={28} className="text-white/80 mb-3" />
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
              {slide.subtitle && <p className="text-base md:text-lg text-white/90 mt-2 max-w-lg drop-shadow">{slide.subtitle}</p>}
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3" style={{ color: theme.primary }}>{slide.title}</h2>
              {slide.intro && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro}</p>}
              {slide.highlight && (
                <div className="p-2.5 rounded-lg text-[13px] italic mb-2" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}`, color: theme.text }}>
                  {slide.highlight}
                </div>
              )}
              {slide.intro2 && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro2}</p>}
              {slide.points && (
                <ul className="space-y-1 mb-2">
                  {slide.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: theme.text }}>
                      <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: theme.primary }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              {slide.intro3 && <p className="text-[13px] leading-relaxed mb-2" style={{ color: theme.text }}>{slide.intro3}</p>}
              {slide.points2 && (
                <ul className="space-y-1 mb-2">
                  {slide.points2.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: theme.text }}>
                      <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: theme.secondary }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );

      case 'grid':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="grid grid-cols-2 gap-2">
                {slide.items.map((item, i) => (
                  <div key={i} className="p-2.5 rounded-lg text-[12px] leading-snug" style={{ background: 'white', border: `1px solid ${theme.primary}22` }}>
                    <span className="font-bold mr-1" style={{ color: theme.primary }}>{item.num}.</span>
                    <span style={{ color: theme.text }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div className="p-5 h-full overflow-auto flex flex-col items-center justify-center" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-2 mb-3">
                {slide.wrong.map((item, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 rounded-lg text-[13px] bg-red-50 border border-red-200">
                      <span className="text-red-700">{item.label}</span>
                    </div>
                    <div className="p-2.5 rounded-lg text-[13px] bg-green-50 border border-green-200">
                      <span className="text-green-700">{item.correct}</span>
                    </div>
                  </div>
                ))}
              </div>
              {slide.highlight && (
                <div className="p-3 rounded-lg text-[13px] italic text-center" style={{ background: theme.primary + '12', borderLeft: `3px solid ${theme.primary}` }}>
                  {slide.highlight}
                </div>
              )}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(30,64,175,0.65)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">{slide.message}</p>
              <div className="space-y-1 w-full mb-3">
                {slide.details.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
                    <Shield size={12} className="text-white flex-shrink-0" />
                    <span className="text-white text-[13px]">{d}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-xl w-full" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-sm font-semibold" style={{ color: theme.primary }}>
                  Rendez-vous dans le Quiz BC2 pour valider vos acquis !
                </p>
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="caferuis-bc2-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bc2-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bc2-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bc2-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CAFBC2Presentation;
